export const en = {
  // App level
  appSubtitle: "AI Agent Hub",

  // Authentication
  loginRequired: "Please log in to access the backoffice",
  secureLogin: "Secure Login",
  loginDescription: "Use your organization credentials to access the system",
  loginWithSSO: "Login with SSO",
  loginHelp: "You will be redirected to your organization's login page",
  loading: "Loading...",
  initializingApp: "Initializing application...",
  redirecting: "Redirecting...",
  processingLogin: "Processing Login...",
  pleaseWait: "Please wait while we complete your login",
  uploadingFiles: "Uploading files...",
  processingFiles: "Please wait while we process your files...",
  loginSuccessful: "Login Successful!",
  redirectingToApp: "Redirecting to the application...",
  loginFailed: "Login Failed",
  loginError: "An error occurred during login",
  tryAgain: "Try Again",
  logout: "Logout",
  loggingOut: "Logging out...",

  // Access Control
  accessDenied: "Access Denied",
  adminAccessRequired:
    "You need administrator privileges to access this section.",
  goBack: "Go Back",

  // 404 Page
  pageNotFound: "Page Not Found",
  pageNotFoundMessage:
    "The page you're looking for doesn't exist or you don't have permission to access it.",
  goHome: "Go Home",
  noFilesYet: "No files uploaded yet",

  user: "User",
  regularUser: "Regular User",
  administrator: "Administrator",
  regularUserDescription:
    "Can modify user-editable settings (creativity, streaming delays, error messages)",
  administratorDescription:
    "Full access to all configurations and system settings",
  permissions: "Permissions:",

  // Header
  changeRole: "Change Role",

  // Homepage
  welcomeBack: "Welcome back",
  homepageDescription: "Select a section to get started",
  dashboardDescription: "Overview of your AI Agent system",
  superAdminDashboard: "Platform overview and tenant statistics",
  totalTenants: "Total Tenants",
  activeTenants: "Active Tenants",
  totalAccountsSA: "Total Accounts",
  totalChatsSA: "Total Chats",
  tenantBreakdown: "Tenant Breakdown",
  usageOverview: "Usage Overview",
  usageOverviewMessages: "Usage Overview — Messages",
  usageOverviewDocuments: "Usage Overview — Documents",
  usageOverviewCharacters: "Usage Overview — Characters in Documents",
  withLimits: "With Limits",
  noTenantsWithLimits: "No tenants with usage limits",
  recentTenants: "Recent Tenants",
  noTenantsYet: "No tenants created yet",
  enabled: "Enabled",
  disabled: "Disabled",
  unlimited: "Unlimited",
  totalFiles: "Total Files",
  totalProducts: "Total products",
  activeStoresLabel: "Active stores",
  ingestionStatusLabel: "Ingestion status",
  filesByCollection: "Files by collection",
  fileTypes: "File types",
  ingestion: "Ingestion",
  scheduler: "Scheduler",
  recentFiles: "Recent Files",
  viewAll: "View All",

  // Main sections
  home: "Home",
  getStarted: "Get Started",
  configurationManagement: "Configuration Management",
  fileManagement: "File Management",
  configurationManagementDescription:
    "Manage AI agent configurations, vector stores, workflows, and prompts",
  fileManagementDescription: "Upload and manage files in the vector store",
  generalInformation: "General Information",
  campaigns: "Campaigns",
  products: "Products",
  internalProcedures: "Internal Procedures",
  internalChat: "Internal Chat",

  // Campaign dates and status
  startDate: "Available to AI from",
  endDate: "Available to AI until",
  campaignStatus: "Status",
  campaignActive: "Active",
  campaignPending: "Not yet active",
  campaignExpired: "Expired",
  selectStartDate: "Select start date",
  selectEndDate: "Select end date",
  noEndDate: "If not set, the AI Agent can use the content indefinitely",
  fileSettings: "File Settings",
  from: "From",
  until: "Until",
  sourceLinkPlaceholder: "e.g. https://docs.example.com/page",
  perFileDescription: "Each file gets its own dates and source links below.",
  datesUpdatedSuccessfully: "Dates updated successfully",
  failedToUpdateDates: "Failed to update dates",
  startDateMustBeBeforeEndDate: "Start date must be before end date",
  campaignDates: "AI Agent Knowledge Dates",
  campaignDatesDescription:
    "Configure availability and source for {count} file{plural}.",
  startDateOptional: "If not set, the AI Agent can use the content immediately",
  sourceUrlInvalid: "Invalid source URL. Must start with http:// or https://.",
  sourceUrlTooLong: "Source URL too long (max 2048 characters).",
  sourceUrlsLabel: "Source links",
  sourceUrlsHelper: "URLs where users can read more about this file.",
  addLink: "Add link",
  removeLink: "Remove link",
  sourceLinkNumbered: "Source link {n} of {total}",
  removeLinkNumbered: "Remove source link {n} of {total}",
  sourceColumnLink: "Link",
  sourceColumnName: "Name",
  sourceLabelPlaceholder: "e.g. Regulation",
  sourceLabelNumbered: "Name for source link {n} of {total}",
  sourceLabelTooLong: "Source name too long (max 80 characters).",
  sourceLabelsHint: "With several links, a short name helps the assistant pick the right one.",
  sourceUrlsTooMany: "At most {max} links per file.",
  sameDatesForAllFiles: "Same dates for all files",
  perFileSettings: "Per-file settings",
  sourceLinkLabel: "Source",
  createdAt: "Created at",
  createdBy: "Created by",
  lastUpdatedBy: "Updated by",
  at: "at",
  lastUpdatedAt: "Last updated",
  dateFilters: "Date Filters (AI Agent)",

  // Toast messages
  loggedInAs: "Logged in as {role}",
  failedToLoadConfigurations: "Failed to load configurations",
  loadingConfigurations: "Loading configurations...",
  switchingChat: "Switching chat...",
  configurationUpdated: 'Configuration "{configType}" updated successfully',
  failedToUpdateConfiguration: "Failed to update configuration",
  failedToUpdate: "Failed to update {configType}: {message}",
  noPermissionToModify:
    "You do not have permission to modify this configuration",

  // Common
  error: "Error",
  success: "Success",
  close: "Close",
  yes: "Yes",
  no: "No",
  save: "Save",
  cancel: "Cancel",
  edit: "Edit",
  saving: "Saving...",
  delete: "Delete",
  deleting: "Deleting...",

  // Language selector
  language: "Language",
  english: "English",
  portuguese: "Português",

  // Config management
  noConfigurationsAvailable: "No configurations available for your role",
  noConfigurationsFound: "No configurations found in the system",
  regularUserAccessInfo:
    "You have access to user-editable settings (creativity, streaming delays, error messages)",
  accessInformation: "Access Information",
  adminAccessInfo:
    "You have full access to all configuration types. You can modify any setting.",
  noPermissionToModifyConfig:
    "You don't have permission to modify this configuration.",
  regularUserPermissionInfo:
    " Regular users can only modify user-editable settings (creativity, streaming delays, error messages).",
  switchToAdminForFullAccess: " Switch to Administrator role for full access.",

  // Configuration types
  vectorStoreSettings: "Vector Store Settings",
  vectorStoreDescription: "Configure vector store and RAG settings",
  userEditableSettings: "User Editable Settings",
  userEditableDescription:
    "Configure creativity, streaming delays, and error messages",
  genericTools: "Generic Tools",
  platformTools: "Platform Tools",
  platformToolsNeedPlatform: "Platform tools need a platform type — set it below and save to enable them.",
  otherTools: "Other",
  voiceToolsNeedChannel: "Voice tools need the voice channel enabled — turn it on and save to enable them.",
  workflowSettings: "Workflow Settings",
  workflowSettingsDescription: "Configure conversation and workflow parameters",
  promptTemplates: "Prompt Templates",
  promptTemplatesDescription: "Configure AI agent prompt templates",
  configurationSettings: "Configuration settings",
  readOnly: "Read Only",
  fullAccess: "Full Access",
  editSettings: "Edit Settings",
  notConfigured: "Not configured",
  notConfiguredTitle: "This chat has no stored row for this configuration type",
  notConfiguredDescription:
    "Nothing is stored, so there are no fields to show. Creating it writes the row for this chat, and the fields then appear with the platform defaults. Review them and save before this chat serves traffic.",
  createConfiguration: "Create Configuration",
  creatingConfiguration: "Creating...",
  saveChanges: "Save Changes",
  saved: "Saved",
  unsavedChanges: "You have unsaved changes",
  discard: "Discard",
  invalidJson: "Invalid JSON",
  invalidJsonFile: "Invalid JSON file — expected an object with key-value pairs",
  characters: "characters",
  export: "Export",
  import: "Import",

  // Config field groups
  groupCollection: "Collection",
  groupProcessing: "Processing",
  groupBehavior: "Behavior",
  groupMessages: "Messages",
  groupLimits: "Limits",
  groupCore: "Core Prompts",
  groupOther: "Other Settings",
  groupRetrieval: "Retrieval Settings per Collection",
  groupProductSettings: "Product Feed",
  maxDocuments: "Max Documents",
  similarityThreshold: "Similarity Threshold",

  // Config field descriptions
  fieldDescCollectionName: "Name of the vector store collection used for RAG",
  fieldDescEmbeddingModel: "AI model used for generating text embeddings",
  fieldDescChunkSize: "Maximum size of text chunks for embedding",
  fieldDescChunkOverlap: "Overlap between consecutive text chunks",
  fieldDescSimilarityTopK: "Number of similar results to retrieve",
  fieldDescCreativity: "Controls AI response creativity (0 = precise, 1 = creative)",
  fieldDescStreamingDelay: "Delay in ms between streamed response chunks",
  fieldDescInternalChat: "Enable internal chat mode for this agent",
  fieldDescErrorMessage: "Custom error message shown to users when something fails",
  fieldDescWelcomeMessage: "Message displayed when a user starts a conversation",
  fieldDescMaxTurns: "Maximum number of turns in a single conversation",
  fieldDescMaxTokens: "Maximum tokens allowed per AI response",
  fieldDescTimeout: "Timeout in seconds for AI response generation",
  fieldDescSystemPrompt: "Main system prompt that defines the AI agent behavior",
  fieldDescContextPrompt: "Additional context injected into the conversation",

  // Role permissions
  allConfigurations: "All Configurations",
  systemSettings: "System Settings",
  advancedOptions: "Advanced Options",

  // File management
  failedToLoadFiles: "Failed to load files",

  // File upload
  upload: "Upload",
  uploadFiles: "Upload Files",
  uploadFilesDescription:
    "Upload files to add them to the vector store. Supported formats:",
  dragAndDrop: "Drag and drop files here, or",
  dropFilesHere: "Drop files here",
  browseFiles: "browse files",
  selectFiles: "Select Files",
  selectMultipleFiles: "You can select multiple files",
  supportedFormats: "Supported formats:",
  uploading: "Uploading...",
  uploadingFile: "Uploading file...",
  uploadingAndProcessing:
    "Uploading and processing {fileName} ({current} of {total})",
  preparingNextFile: "Preparing to upload next file...",
  fileUploadedSuccessfully: "{fileName} uploaded and processed successfully",
  uploadSuccess: "Files uploaded successfully",
  uploadError: "Error uploading files",
  uploadFailed: "Upload failed",
  failedToUpload: "Failed to upload {fileName}: {error}",
  someFilesCouldNotBeUploaded: "Some files could not be uploaded: {errors}",
  fileTypeNotSupported: "File type {extension} is not supported",
  fileSizeTooLarge: "File size too large (max 50MB)",
  pdfPageLimitExceeded: "PDF has {totalPages} pages, maximum allowed is {maxPages}",
  allFilesUploadedSuccessfully:
    "File uploaded and processed successfully!",
  allFilesUploadedSuccessfullyPlural:
    "All {count} files uploaded and processed successfully!",
  uploadComplete: "Upload complete: {successful} successful, {failed} failed",
  uploadFailedCount:
    "Upload failed: {count} file{plural} could not be processed",
  queued: "Queued",
  completed: "Done",
  failed: "Failed",
  deleted: "Deleted",
  rejected: "Rejected",
  deleteAndRejectComplete: "{deleted} deleted and {rejected} rejected for this store.",
  deletingFiles:
    "Deleting {fileName} ({current} of {total})",
  allFilesDeletedSuccessfully:
    "All {count} file{plural} deleted successfully!",
  deleteComplete: "Delete complete: {successful} successful, {failed} failed",

  // File list
  fileList: "File List",
  noFiles: "No files available",
  uploadedFiles: "Uploaded Files",
  noFilesUploaded: "No files uploaded",
  noFilesOrFolders: "No files or folders",
  uploadFilesToSeeThem: "Upload files to see them listed here.",
  uploadFilesOrCreateFolder: "Upload files or create a folder to get started.",
  readOnlyLocationHint: "This location is read only. Contact your administrator if you need access.",
  filesCount: "{filtered} of {total} file{plural}",
  sort: "Sort",
  filters: "Filters",
  selected: "{count} selected",
  deleteSelected: "Delete Selected",
  downloadAll: "Download All",
  downloadAllSuccess: "Successfully downloaded {count} file{plural}",
  downloadAllPartial: "Downloaded {successful} files, {failed} failed",
  downloadZipSuccess: "ZIP file created successfully with {count} file{plural}",
  downloadZipPartial: "ZIP created with {successful} files, {failed} failed",
  downloadZipError: "Failed to create ZIP file",
  sortBy: "Sort by",
  name: "Name",
  type: "Type",
  size: "Size",
  date: "Date",
  searchByName: "Search by name",
  enterFileName: "Search all files...",
  fileType: "File type",
  allTypes: "All types",
  allStatuses: "All Statuses",
  chat: "Chat",
  allChats: "All chats",
  dateRange: "Date range",
  clearAllFilters: "Clear all filters",
  selectAll: "Select All",
  deselectAll: "Deselect All",
  deleteFile: "Delete file",
  downloadFile: "Download file",
  viewFile: "View file",
  deleteMultipleFiles: "Delete Multiple Files",
  deleteMultipleFilesConfirmation:
    "Are you sure you want to delete {count} file{plural}?",
  filesToBeDeleted: "Files to be deleted:",
  deleteActionWarning:
    "This action cannot be undone. The files will be permanently removed from the vector store.",
  deleteCampaignWarning:
    "VTEX campaigns are not deleted, they are rejected for this store. The file goes if no other store publishes it, and the rejection can be undone in the sync panel.",
  deleteFiles: "Delete {count} File{plural}",
  failedToDeleteFile: "Failed to delete file: {error}",
  errorDeletingFile: "Error deleting file: {error}",
  deleteConfirmation: "Are you sure you want to delete this file?",
  fileDeleted: "File deleted successfully",

  // File viewer
  failedToLoadFileContent: "Failed to load file content",
  file: "file",
  download: "Download",
  preparingDownload: "Preparing download...",
  downloadingFiles: "Downloading files",
  creatingZip: "Creating ZIP file...",
  errorLoadingFile: "Error loading file",
  page: "page{plural}",
  scrollToNavigate: "Scroll to navigate",
  zoom: "Zoom",
  failedToLoadPDF: "Failed to load PDF",

  // File editing
  fileSaved: "File saved successfully",
  failedToSaveFile: "Failed to save file",
  editFile: "Edit File",
  editingFile: "Editing {fileName}",
  unauthorizedToEditFile: "You are not authorized to edit this file",
  permissionDeniedToEditFile: "Permission denied to edit this file",
  fileNotFoundForEdit: "File not found for editing",
  fileTooLargeToEdit: "File is too large to edit",
  serverErrorWhileSaving: "Server error occurred while saving the file",
  networkErrorWhileSaving: "Network error occurred while saving the file",
  deleteFileConfirmation: "Are you sure you want to delete {fileName}?",
  fileToBeDeleted: "File to be deleted",

  // Footer
  quickLinks: "Quick Links",
  contactAndInfo: "Contact & Info",
  allRightsReserved: "All rights reserved.",

  // Products Management
  productsManagement: "Products Management",
  productsManagementDescription: "Manage product ingestion and scheduler",
  clearCache: "Clear Cache",
  clearCacheDescription: "Clear all cached data",
  clearProductsCache: "Products Cache",
  clearProductsCacheDescription: "Clear products-specific cache",
  clearGeneralInformationCache: "General Information Cache",
  clearGeneralInformationCacheDescription:
    "Clear general information-specific cache",
  clearCampaignsCache: "Campaigns Cache",
  clearCampaignsCacheDescription: "Clear campaigns-specific cache",
  clearInternalProceduresCache: "Internal Procedures Cache",
  clearInternalProceduresCacheDescription: "Clear internal procedures-specific cache",
  clearButton: "Clear Cache",
  clearButtonLoading: "Clearing...",
  confirmClearCache: "Confirm Clear",
  confirmClearCacheMessage:
    "Are you sure you want to clear the {cacheType} cache?",
  cacheClearedSuccess: "{cacheType} cache cleared successfully!",
  cacheActions: "Chat Cache Actions",
  cacheActionsDescription: "Clear cache by specific content type",
  runIngestion: "Run Ingestion",
  runIngestionDescription: "Manually trigger product ingestion from feed",
  startScheduler: "Start Scheduler",
  startSchedulerDescription: "Start the product ingestion scheduler",
  stopScheduler: "Stop Scheduler",
  stopSchedulerDescription: "Stop the product ingestion scheduler",
  schedulerStatus: "Scheduler Status",
  schedulerRunning: "Scheduler Running",
  productIngestionJob: "Product Ingestion Job",
  running: "Running",
  stopped: "Stopped",
  cacheClearedSuccessfully: "Cache cleared successfully",
  ingestionStartedSuccessfully: "Product ingestion started in the background",
  schedulerStartedSuccessfully:
    "Product ingestion scheduler started successfully",
  schedulerStoppedSuccessfully:
    "Product ingestion scheduler stopped successfully",
  failedToClearCache: "Failed to clear cache",
  failedToStartIngestion: "Failed to start product ingestion",
  failedToStartScheduler: "Failed to start scheduler",
  failedToStopScheduler: "Failed to stop scheduler",
  failedToFetchSchedulerStatus: "Failed to fetch scheduler status",
  connectionError: "Connection Error",
  status: "Status",
  intervalMinutes: "Interval",
  intervalHiddenByDailyTimesMode:
    "The schedule runs at fixed times, so this interval is not used. It is still stored and still saved; set the mode back to 'interval' to edit it. It is still validated, though: a value of 0 or less refuses the schedule.",
  dailyTimesHiddenByIntervalMode:
    "The schedule runs on an interval, so the fixed times are not used. They are still stored and still saved; set the mode to 'daily_times' to edit them.",
  dailyTimes: "Daily times",
  atTimes: "At",
  minutes: "minutes",
  nextRunTime: "Next Run",
  nextRun: "Next Run",
  lastIngestion: "Last Ingestion",
  ingestionStatus: "Ingestion Status",
  manualIngestionStatus: "Manual Ingestion Status",
  manualIngestionDescription:
    "Shows status of manually triggered ingestions only",
  startedAt: "Started At",
  completedAt: "Completed At",
  noIngestionRunning: "No ingestion currently running",
  noManualIngestion: "No manual ingestion running",
  clickRunToStart: "Click 'Run Ingestion' to start processing products",
  clickRunToStartManual: "Click 'Run Ingestion' to manually process products",
  scheduledIngestionNote:
    "Note: Scheduled ingestions are managed by the scheduler above",

  // Products Management — KPI, History, Logs
  inVectorStore: "In vector store",
  schedulerLabel: "Scheduler",
  every: "Every",
  idle: "Idle",
  noDataYet: "No data yet",
  ingestionStarted: "Ingestion started...",
  ingestionCompleted: "Ingestion completed successfully",
  ingestionFailed: "Ingestion failed",
  triggeringIngestion: "Triggering product ingestion...",
  ingestionTriggered: "Ingestion triggered, waiting for response...",
  ingestionLogs: "Ingestion Logs",
  ingestionHistory: "Ingestion History",
  ingestionHistoryDescription: "Recent ingestion runs from this session",
  duration: "Duration",
  details: "Details",

  // Conversations
  conversations: "Conversations",
  conversationsEmpty: "No conversations yet",
  conversationsEmptyHint:
    "Conversations show up here once a chat runs in kodly_direct mode",
  conversationsLoadFailed: "Could not load the conversations",
  conversationsAnonymous: "Anonymous",
  conversationsStatusOpen: "Open",
  conversationsStatusResolved: "Resolved",
  conversationsStatusHandedOff: "Handed off",
  conversationsBot: "Bot",
  conversationsBotTitle: "Started by an automated client (a crawler or a test), left out of the analytics counts",
  conversationsAll: "All",
  conversationsAllChats: "All chats",
  conversationsBack: "Back to conversations",
  conversationsTranscriptLabel: "Conversation transcript",
  conversationsNotFound: "Conversation not found",
  conversationsDetailFailed: "Could not load the conversation",
  conversationsRoleUser: "Customer",
  conversationsRoleAssistant: "Agent",
  conversationsRoleSystem: "System",
  conversationsRoleTool: "Tool",
  conversationsRoleOperator: "Operator",
  // The divider where the conversation passed to an operator in Chatwoot.
  conversationsHandedOffTo: "Handed off to {name} at {time}",
  conversationsHandedOffToSomeone: "Handed off to an operator at {time}",
  conversationsPageMarker: "On page {path}",
  // Under a customer's message the bridge has not delivered to the operator.
  conversationsBridgeLate: "Not yet delivered to the operator",
  conversationsBridgeRejected: "Not delivered to the operator",
  conversationsBridgeUncertain: "May not have reached the operator",
  // At the foot of a conversation that passed to an operator.
  conversationsOpenInChatwoot: "Open in Chatwoot",
  conversationsOpensInNewTab: "(opens in a new tab)",
  conversationsCount: "{count} conversation{plural}",
  conversationsNoMessages: "No messages",
  conversationsPickOne: "Pick a conversation",
  conversationsPickOneHint: "The transcript shows up here",
  conversationsStartedAgo: "Started {when}",
  conversationsKindImage: "Image",
  conversationsKindVoice: "Voice message",
  conversationsKindDocument: "Document",
  conversationsAttachmentDownload: "Download",
  conversationsAttachmentLoading: "Loading",
  conversationsAttachmentFailed: "Could not load. Try again",
  // The list header and its filters. The status buttons reuse the status keys
  // above; these are the chrome around them.
  conversationsFilters: "Filters",
  conversationsFiltersActive: "{count} active filter{plural}",
  conversationsClearFilters: "Clear filters",
  conversationsStatusGroup: "Filter by status",
  conversationsPeriod: "Period",
  conversationsPeriodAny: "Any time",
  conversationsPeriodLast7: "Last 7 days",
  conversationsPeriodLast30: "Last 30 days",
  conversationsPeriodCustom: "Pick dates",
  conversationsPeriodRange: "{from} to {to}",
  conversationsChat: "Chat",
  conversationsRemoveFilter: "Remove filter {label}",
  // The CSAT filters (D16 of the CSAT on the page plan). The operator and "assistant only"
  // only arrive by the analytics page's link, and show only as chips.
  conversationsCsatFilter: "Rating",
  conversationsCsatAny: "Any",
  conversationsCsatRated: "Rated",
  conversationsCsatCommented: "Only with a comment",
  conversationsPeriodByStart: "Started",
  conversationsPeriodByClose: "Closed",
  conversationsPeriodBy: "Count by start or by close",
  conversationsChipClosed: "Closed: {period}",
  conversationsChipOperator: "Operator: {name}",
  conversationsChipOperatorUnnamed: "Rated operator",
  conversationsChipVoice: "With a voice message",
  conversationsChipAssistantOnly: "Assistant only",
  // The filter buttons name the SET, the status keys name one conversation.
  // Same word in English, a different number in Portuguese.
  conversationsFilterOpen: "Open",
  conversationsFilterResolved: "Resolved",
  conversationsFilterHandedOff: "Handed off",
  conversationsEmptyFiltered: "Try another status or clear the filters",
  conversationsRetry: "Try again",
  // The note that the agent is typing, under the last turn.
  conversationsAgentTyping: "The agent is typing",
  // Day dividers in the transcript. Older days come from Intl, these two are
  // ours so the capital letter is ours too.
  conversationsToday: "Today",
  conversationsYesterday: "Yesterday",
  // The contact column can be shown and hidden from the transcript header.
  conversationsShowContact: "Show contact",
  conversationsHideContact: "Hide contact",
  // Third column: who the contact behind the conversation is. Operator chrome,
  // so it follows the backoffice language, not the widget one.
  conversationsContactPanel: "Contact",
  conversationsContactEmail: "Email",
  conversationsContactPhone: "Phone",
  conversationsContactIdentifier: "Identifier",
  conversationsContactFirstSeen: "First seen",
  conversationsContactHistory: "Previous conversations",
  conversationsContactFirstConversation: "First conversation from this contact",
  conversationsContactHistoryMore: "This contact has more conversations beyond these {count}",
  conversationsContactHistoryFailed: "Could not load the previous conversations",
  // Context attributes: what the shopper's browser sent with that turn, not
  // stored properties of the contact. The heading has to say when, otherwise it
  // reads as a permanent record the platform never keeps. Key labels come from
  // the keys themselves (utils/contextAttributes.js). Only what the platform
  // writes has a translated label: the page, the device, the languages, the cart,
  // the technical details (IP, User-Agent) and the profile name (Name).
  conversationsContextHeading: "Context from the last message",
  conversationsCustomerProfileHeading: "Customer profile (cookie information)",
  conversationsContextPage: "Page",
  conversationsContextItemsOne: "1 item",
  conversationsContextItems: "{count} items",
  conversationsContextFieldsOne: "1 field",
  conversationsContextFields: "{count} fields",
  conversationsContextDevice: "Device",
  conversationsContextLanguages: "Languages",
  conversationsContextPageLanguage: "Page in {language}",
  conversationsContextBrowserLanguage: "Browser in {language}",
  conversationsContextTechnical: "Technical details",
  conversationsContextCopyUserAgent: "Copy User-Agent",
  conversationsContextCopied: "Copied",
  conversationsCartItemsOne: "1 item",
  conversationsCartItems: "{count} items",
  conversationsCartEmpty: "Empty cart",
  conversationsCartItemFallback: "Item {id}",
  conversationsCartToOffer: "To offer: {list}",
  conversationsCartShowOthers: "Show the other {count}",
  conversationsCartUnits: "{qty} units",
  conversationsCart: "Cart",
  conversationsProfileName: "Name",
  conversationsContextIp: "IP",
  conversationsContextCopyIp: "Copy IP",
  conversationsContextUserAgentLabel: "User-Agent",
  conversationsDeviceMobile: "Mobile",
  conversationsDeviceTablet: "Tablet",
  conversationsDeviceDesktop: "Desktop",
  conversationsDeviceOther: "Other device",
  conversationsContextMore: "{count} more",
  // Customer rating (CSAT) card at the end of a closed conversation.
  conversationsCsatTitle: "Customer rating",
  conversationsCsatHandledBy: "Handled by {name}",
  conversationsCsatStarsLabel: "{n} of 5 stars",
  // Product cards in the transcript (ported from the widget, same copy the
  // shopper saw, minus the add to cart button).
  productCardViewProduct: "View product",
  productCardSizesLabel: "Size:",
  productCardPrevious: "Previous product",
  productCardNext: "Next product",
  productCardCarouselLabel: "Recommended products",
  productCardRecommendedPriceLabel: "RRP",
  productCardPriceBadgeOverRecommended: "vs. RRP",
  productCardPriceBadgeDirect: "DIRECT",
  productCardDeliveryBusinessDays: "Delivery in {min} to {max} business days",
  productCardDeliveryBusinessDaysExact: "Delivery in {days} business days",
  productCardDeliveryBusinessDaysOne: "Delivery in 1 business day",
  productCardDeliveryUpTo: "Delivery in up to {max} days",
  productCardDeliveryUpToOne: "Delivery in up to 1 day",
  productCardAvailabilityTemporarilyUnavailable: "Temporarily unavailable",
  productCardAvailabilityPreorder: "Ships on release date",
  productCardPriceInfo: "Price information",

  // Store Locations Management
  totalStores: "Total Stores",
  activeStores: "Active",
  inactiveStores: "Inactive",
  storeLocations: "Store Locations",
  storeLocationsManagement: "Store Locations Management",
  storeLocationsManagementDescription: "Import and manage store locations",
  storeLocationsDescription: "Import store locations from a JSON file",
  storeLocationsDragDrop: "Drag and drop a JSON file here, or click to select",
  storeLocationsSelectFile: "Select JSON File",
  storeLocationsUploading: "Uploading and importing stores...",
  storeLocationsFileFormat:
    "File format: JSON array with store objects. All fields (id, nome, morada, cod_postal, latitude, longitude) are required.",
  storeLocationsFieldId: "Store ID (required)",
  storeLocationsFieldNome: "Store name (required)",
  storeLocationsFieldMorada: "Address (required)",
  storeLocationsFieldCodPostal: "Postal code (required)",
  storeLocationsFieldLatitude: "Latitude (required)",
  storeLocationsFieldLongitude: "Longitude (required)",
  storeLocationsInvalidFileType: "File must be a JSON file",
  storeLocationsFileTooLarge: "File size must be less than 10MB",
  storeLocationsImportSuccess: "Stores imported successfully",
  storeLocationsImportSuccessWithCount: "{count} stores imported successfully",
  storeLocationsImportFailed: "Failed to import stores",
  storeLocationsImportError: "Error importing store locations",
  storeLocationsImportedCount: "{count} stores imported",
  storeLocationsInstructions: "Instructions",
  storeLocationsInstructions1:
    "1. Prepare a JSON file with an array of store objects",
  storeLocationsInstructions2:
    "2. Each store object must contain the following fields:",
  storeLocationsInstructions3: "3. Upload the file using the area above",
  storeLocationsFetchError: "Error fetching stores",
  storeLocationsCreateSuccess: "Store created successfully",
  storeLocationsCreateError: "Error creating store",
  storeLocationsUpdateSuccess: "Store updated successfully",
  storeLocationsUpdateError: "Error updating store",
  storeLocationsDeleteSuccess: "Store deleted successfully",
  storeLocationsDeleteError: "Error deleting store",
  storeLocationsDeleteConfirm: "Are you sure you want to delete this store?",
  storeLocationsToggleSuccess: "Store status updated successfully",
  storeLocationsToggleError: "Error updating store status",
  storeLocationsAddStore: "Add Store",
  storeLocationsSearchPlaceholder: "Search stores...",
  storeLocationsIncludeInactive: "Include inactive",
  storeLocationsTableId: "ID",
  storeLocationsTableName: "Name",
  storeLocationsTableAddress: "Address",
  storeLocationsTablePostalCode: "Postal Code",
  storeLocationsTableStatus: "Status",
  storeLocationsTableActions: "Actions",
  storeLocationsActive: "Active",
  storeLocationsInactive: "Inactive",
  storeLocationsDeactivate: "Deactivate",
  storeLocationsActivate: "Activate",
  storeLocationsEdit: "Edit",
  storeLocationsDelete: "Delete",
  storeLocationsNoStores:
    "No stores found. Add your first store to get started.",
  storeLocationsNoResults: "No stores match your search criteria.",
  storeLocationsCreateStore: "Create Store",
  storeLocationsEditStore: "Edit Store",
  storeLocationsFormId: "Store ID",
  storeLocationsFormName: "Name",
  storeLocationsFormAddress: "Address",
  storeLocationsFormPostalCode: "Postal Code",
  storeLocationsFormLatitude: "Latitude",
  storeLocationsFormLongitude: "Longitude",
  storeLocationsFormActive: "Active",
  storeLocationsImportFromFile: "Import from JSON",
  storeLocationsTabList: "List",
  storeLocationsTabMap: "Map",
  storeLocationsMapInstructions:
    "Click on a marker on the map or a store card below to view details.",
  storeLocationsViewDetails: "View Details",
  storeLocationsStoreSingular: "store",
  storeLocationsStorePlural: "stores",
  storeLocationsShown: "shown",
  storeLocationsMapOverlayHint: "Click on store cards below to view details",
  storeLocationsViewOnMap: "View on Google Maps",
  storeLocationsStoreDetails: "Store Details",
  storeLocationsCreatedAt: "Created At",
  storeLocationsUpdatedAt: "Updated At",
  storeLocationsExportToJson: "Export to JSON",
  storeLocationsExportSuccess: "Stores exported successfully",
  storeLocationsExportError: "Error exporting stores",
  storeLocationsExporting: "Exporting stores...",
  storeStockCheckStock: "Check Stock",
  storeStockModalTitle: "Check Stock",
  storeStockProductIdLabel: "Product ID",
  storeStockProductIdPlaceholder: "Enter product ID",
  storeStockProductIdRequired: "Please enter a product ID",
  storeStockCheckButton: "Check",
  storeStockChecking: "Checking...",
  storeStockCheckError: "Error checking stock",
  storeStockResult: "Result",
  storeStockProductId: "Product",
  storeStockQuantity: "Quantity",
  storeStockStatus: "Status",
  storeStockNoStockStatus: "No Stock",
  storeStockLastUnits: "Last units",
  storeStockLowStock: "Few units",
  storeStockManyUnits: "Many units",
  storeStockLastUnitsWarning: "⚠️ Last units available",
  storeStockLowStockWarning: "⚠️ Few units available",
  storeStockNoStock: "No stock available",
  storeLocationsTabWarehouses: "Warehouses",
  warehousesTitle: "Warehouses and Logistics Centre",
  warehousesAddWarehouse: "Add Warehouse",
  warehousesEditWarehouse: "Edit Warehouse",
  warehousesCenterId: "SAP Code",
  warehousesName: "Name",
  warehousesAddress: "Address",
  warehousesPostalCode: "Postal Code",
  warehousesActive: "Active",
  warehousesIncludeInactive: "Include inactive",
  warehousesEmpty: "No warehouses configured yet",
  warehousesFetchError: "Error fetching warehouses",
  warehousesCreateSuccess: "Warehouse created",
  warehousesCreateError: "Error creating warehouse",
  warehousesUpdateSuccess: "Warehouse updated",
  warehousesUpdateError: "Error updating warehouse",
  warehousesDeleteSuccess: "Warehouse removed",
  warehousesDeleteError: "Error removing warehouse",
  warehousesDeleteConfirm: "Remove this warehouse?",
  warehousesCheckStock: "Check stock",
  warehousesStockProductId: "Product SAP Code",
  warehousesStockResult: "Units in stock",
  warehousesStockError: "Error checking stock",
  cacheManagement: "Cache Management",
  cacheManagementDescription: "Manage Redis cache for your tenant",
  connected: "Connected",
  disconnected: "Disconnected",

  // Similarity Search
  similaritySearch: "Similarity Search",
  internalProceduresDescription: "Search through internal procedures and administrative information",
  similaritySearchDescription:
    "Search for similar content using natural language queries",
  selectCollection: "Select Collection",
  generalInformationDescription:
    "Search through general company information and policies",
  campaignsDescription:
    "Search through marketing campaigns and promotional content",
  productsDescription: "Search through product catalog and specifications",
  productsSearchDescription: "Search through product catalog using natural language",
  searchQuery: "Search Query",
  enterSearchQuery: "Enter a search query...",
  resultsLimit: "Results Limit",
  rawSearch: "Raw Search",
  searchFilters: "Search Filters",
  minPrice: "Min Price (€)",
  maxPrice: "Max Price (€)",
  brands: "Brands (comma-separated)",
  availability: "Availability",
  pricePreference: "Price Preference",
  inStock: "In Stock",
  outOfStock: "Out of Stock",
  cheapest: "Cheapest",
  mostExpensive: "Most Expensive",
  any: "Any",
  search: "Search",
  searching: "Searching...",
  searchResults: "Search Results",
  noResultsFound: "No results found",
  noResultsDescription:
    "Try adjusting your search terms or try a different collection",
  searchError: "Search error",
  readyToSearch: "Ready to Search",
  readyToSearchDescription:
    "Try searching for content, descriptions, or features",
  pleaseEnterQuery: "Please enter a search query",
  resultDetails: "Result Details",
  content: "Content",

  // Product Details Modal
  basicInformation: "Basic Information",
  productDetails: "Product Details",
  keyFeatures: "Key Features",
  embeddedText: "Embedded Text",
  metadata: "Metadata",
  similarityScore: "Similarity Score",
  price: "Price",
  brand: "Brand",
  model: "Model",
  stock: "Stock",
  sapCode: "SAP Code",
  ean: "EAN",
  productUrl: "Product URL",
  viewProduct: "View Product",
  units: "units",
  embeddedTextDescription:
    "This is the text that was embedded into the vector store for similarity search",
  noImageAvailable: "No image available",

  // Vector Store Statistics
  vectorStoreStatistics: "Vector Store Statistics",
  vectorStoreStatisticsDescription:
    "Monitor vector store collections and system health",
  totalCollections: "Total Collections",
  totalVectors: "Total Vectors",
  systemHealth: "System Health",
  healthy: "Healthy",
  collectionsDetails: "Collections Details",
  vectors: "Vectors",
  vectorSize: "Vector Size",
  distance: "Distance",
  clear: "Clear",
  clearAll: "Clear All",
  clearCollectionConfirmation:
    'Are you sure you want to clear the "{collectionName}" collection? This action cannot be undone.',
  clearAllCollectionsConfirmation:
    "Are you sure you want to clear ALL collections? This action cannot be undone.",
  failedToFetchVectorStoreStats: "Failed to fetch vector store statistics",
  failedToClearCollection: "Failed to clear collection: {error}",
  failedToClearAllCollections: "Failed to clear all collections: {error}",
  collectionClearedSuccessfully: "Collection cleared successfully!",
  allCollectionsClearedSuccessfully: "All collections cleared successfully!",
  insufficientPermissions:
    "Insufficient permissions. Only administrators can clear collections.",
  collectionNotFound: "Collection not found.",
  serverErrorClearCollection:
    "Server error occurred while clearing collection. Please try again.",
  insufficientPermissionsClearAll:
    "Insufficient permissions. Only administrators can clear all collections.",
  serverErrorClearAllCollections:
    "Server error occurred while clearing all collections. Please try again.",
  unauthorizedAccess: "Unauthorized access. Please log in again.",
  insufficientPermissionsStats:
    "Insufficient permissions to view vector store statistics.",
  serverErrorStats:
    "Server error occurred while fetching statistics. Please try again.",
  networkErrorStats:
    "Network error. Please check your connection and try again.",
  reindexationStatus: "Re-indexation",
  reindexationDescription: "Re-process all files with the current embedding model",
  runReindexation: "Run Re-indexation",
  noReindexationRunning: "No re-indexation running",
  clickRunToStartReindex: "Click the button above to re-process all files",
  systemInformation: "System Information",
  vectorStoreInfo:
    "Vector store statistics are updated in real-time. Use the refresh button to get the latest data. Clearing collections will permanently remove all vectors and cannot be undone.",
  refreshing: "Refreshing...",
  noCollections: "No Collections Found",
  noCollectionsDescription: "No vector store collections are available.",
  actionCannotBeUndone: "This action cannot be undone!",
  allCollections: "All Collections",
  confirm: "Confirm",
  typeToConfirm: "Type to confirm",
  typeConfirmationText: "Type",
  toProceed: "to proceed",

  // Grafana Dashboard
  grafanaDashboard: "Logs Explore",
  grafanaDescription: "AI Agent Logs Explorer",
  grafanaError: "Grafana Dashboard Error",
  grafanaConnectionError:
    "Failed to load Grafana dashboard. Please check if Grafana is running.",
  loadingDashboard: "Loading dashboard...",
  openInNewTab: "Open in New Tab",
  retry: "Retry",
  grafanaAuthRequired: "Grafana Authentication Required",
  grafanaAuthMessage:
    "To access the Grafana logs explorer, you need to authenticate with Grafana first. After logging in, come back to this page and click the 'Refresh After Login' button to load the dashboard.",
  loginToGrafana: "Login to Grafana",
  refreshAfterLogin: "Refresh After Login",

  // Advanced Cache Management (Admin Only)
  advancedCacheManagement: "Advanced Chat Cache Management",
  adminsOnly: "Admins Only",
  advancedCacheManagementDescription:
    "Clear technical system caches - use with caution",
  clearTenantConfigCache: "Tenant Config Cache",
  clearTenantConfigCacheDescription: "Clear tenant configuration cache",
  clearAgentCache: "Agent Cache",
  clearAgentCacheDescription: "Clear agent and agent metadata cache",
  clearChatHistoryCache: "Chat History Cache",
  clearChatHistoryCacheDescription: "Clear all chat history",
  clearIngestionStatusCache: "Ingestion Status Cache",
  clearIngestionStatusCacheDescription: "Clear ingestion status cache",
  clearWorkflowCache: "Workflow Cache",
  clearWorkflowCacheDescription: "Clear workflow configuration cache",
  clearAllTechnicalCache: "Clear All Technical Caches",
  clearAllTechnicalCacheDescription: "Clear all technical caches",
  clearAllButton: "Clear All",

  // Tenant Management
  superAdministrator: "Super Administrator",
  tenantManagement: "Tenant Management",
  tenantManagementDescription: "Manage tenants and their configurations",
  managingTenant: "Managing tenant:",
  exitTenant: "Exit",
  enterTenant: "Enter",
  inactiveTenantCannotEnter: "Inactive tenant, cannot enter",
  tenants: "Tenants",
  createFirstTenant: "Create your first tenant to get started",
  createTenant: "Create Tenant",
  addNewTenant: "Add New Tenant",
  tenantName: "Tenant Name",
  chatwootApiUrl: "Chatwoot API URL",
  chatwootAccountId: "Chatwoot Account ID",
  chatwootInboxId: "Chatwoot Inbox ID",
  chatwootApiToken: "Chatwoot API Token",
  chatwootAgentApiToken: "Chatwoot Agent API Token",
  chatwootWebhookToken: "Chatwoot Webhook Token",
  chatwootHmacToken: "Chatwoot HMAC Token",
  chatwootHmacTokenDescription:
    "Used for identity validation in Chatwoot widget",
  externalAccessApiKey: "External Access API Key",
  externalAccessApiKeyDescription: "API key for external access",
  accountId: "Account ID",
  active: "Active",
  inactive: "Inactive",
  updatedAt: "Updated At",
  actions: "Actions",
  viewDetails: "View Details",
  deactivate: "Deactivate",
  editTenant: "Edit Tenant",
  editTenantDescription: "Leave fields empty to keep current values",
  leaveEmptyToKeepCurrent: "Leave empty to keep current value",
  tenantDetails: "Tenant Details",
  tenantId: "Tenant ID",
  create: "Create",
  creating: "Creating...",
  allFieldsRequired: "All fields are required",
  tenantCreatedSuccessfully: "Tenant created successfully",
  tenantUpdatedSuccessfully: "Tenant updated successfully",
  tenantDeactivatedSuccessfully: "Tenant deactivated successfully",
  failedToFetchTenants: "Failed to fetch tenants",
  failedToCreateTenant: "Failed to create tenant",
  failedToUpdateTenant: "Failed to update tenant",
  failedToDeactivateTenant: "Failed to deactivate tenant",
  failedToFetchTenantDetails: "Failed to fetch tenant details",
  confirmDeactivateTenant:
    'Are you sure you want to deactivate tenant "{name}"?',
  showToken: "Show",
  hideToken: "Hide",
  deactivateTenant: "Deactivate Tenant",
  tenantInformation: "Tenant Information",
  deactivateTenantWarning:
    "This action will deactivate the tenant. The tenant will no longer be able to access the system, but data will be preserved.",
  activate: "Activate",
  tenantActivatedSuccessfully: "Tenant activated successfully",
  failedToActivateTenant: "Failed to activate tenant",
  tokenCopied: "Token copied",
  tokenCopyFailed: "Failed to copy token",
  widgetTokenRotated: "Widget token rotated",
  widgetTokenRotateFailed: "Failed to rotate widget token",
  rotateWidgetToken: "Rotate Widget Token",
  rotateWidgetTokenWarning: "The previous token will stop working immediately.",
  rotate: "Rotate",
  rotating: "Rotating...",

  // Account Management
  accountManagement: "Account Management",
  accounts: "Accounts",

  // Chat Management
  chatManagement: "Chat Management",
  chats: "Chats",
  chatwootChannelOptional: "Chatwoot channel (optional)",
  chatwootChannelOptionalHelp:
    "Leave blank for a voice-only or widget-only chat.",
  chatwootWebhookBaseUrl: "Chatwoot webhook base URL",
  chatwootWebhookBaseUrlHelp:
    "The origin this account's Chatwoot reaches the backend by, with no path. The handoff setup of each chat builds its transfer inbox's webhook from it. Changing it later does not update the inboxes already created, whose chats then show a mismatch.",
  handoffSetupTitle: "Handoff to an operator",
  handoffSetupDescription:
    "Creates this chat's transfer inbox in Chatwoot and records it on the chat. Set up asks first, and then acts at once, without waiting for Save.",
  handoffSetupLoading: "Reading the handoff setup...",
  handoffSetupLoadFailed: "Could not read the handoff setup: {detail}. Close and reopen the dialog to try again.",
  handoffSetupFailed: "Failed to set up the handoff",
  handoffSetupState_not_configured: "Not set up yet, and ready to be.",
  handoffSetupState_blocked: "Cannot be set up until what is listed below is fixed.",
  handoffSetupState_configured: "Set up.",
  handoffSetupState_mismatch: "Set up, but the inbox in Chatwoot no longer matches what the chat records.",
  handoffSetupState_inbox_missing: "Set up, but the inbox the chat records no longer exists in Chatwoot.",
  handoffSetupState_chatwoot_error: "Chatwoot could not be read.",
  handoffSetupChatwootVersion: "Chatwoot {version}",
  handoffSetupInbox: "Inbox {id}: {name}",
  handoffSetupButton: "Set up",
  handoffSetupRunning: "Setting up...",
  handoffSetupConfirmTitle: "Set up the handoff for {name}?",
  handoffSetupConfirmWhat:
    "This creates this chat's transfer inbox in the account's Chatwoot, copies into it the members of the chat's current inbox, and records it on the chat. Undoing it takes deleting the inbox in Chatwoot and clearing three columns with SQL.",
  handoffSetupConfirmOrder:
    "Only go ahead if the widget release that shows the operator (W1) is already live in this environment's widget: an older widget shows the operator's messages as the bot's.",
  handoffSetupConfirmButton: "Create the inbox",
  identityCookieIntegration: "Identity Cookie Integration",
  identityCookieIntegrationDescription:
    "Mirror a first-party cookie into a contact attribute for this chat, so the agent can recognize returning visitors.",
  identityCookieEnabled: "Identity cookie integration enabled",
  cookieName: "Cookie Name",
  attributeKey: "Attribute Key",
  cdpEnrichmentTitle: "Optional: CDP Enrichment (Bloomreach)",
  cdpEnrichmentDescription:
    "Also push the identity to a Customer Data Platform (currently Bloomreach Engagement) for cross-channel personalization.",
  cdpEnrichmentEnabled: "CDP enrichment enabled",
  apiBaseUrl: "API Base URL",
  projectToken: "Project Token",
  apiKeyId: "API Key ID",
  apiSecret: "API Secret",
  bloomreachAttributes: "Attributes (JSON)",
  bloomreachAttributesHelp:
    "Array of attribute definitions sent to Bloomreach, as JSON.",
  loadingIdentityCookieIntegration: "Loading identity cookie integration...",
  failedToFetchIdentityCookieIntegration: "Failed to fetch identity cookie integration",

  // Chat selection for file upload
  selectChatsAccess: "Chats with Access",
  noChatSelectedInfo: "If no chat is selected, the file will NOT be available in any chat.",
  
  // Chat selection in configurations
  selectChat: "Select Chat",
  configsForThisChat: "Viewing configurations for this chat",
  chatConfigInfo: "Chat-specific configurations",
  loadingChatConfigs: "Loading chat configurations...",
  clearCacheForChat: "Clear cache for this chat",
  clearingCacheFor: "Clearing cache for",
  breakdownByChat: "By Chat",
  
  // Chat management modal
  manageChatsForFile: "Manage Chats for File",
  collection: "Collection",
  selectChats: "Select Chats (select one or more)",
  noChatsAvailable: "No chats available",
  noChatsAvailableMessage: "Please ensure chats are configured for this tenant.",
  chatsSelected: "{count} chat(s) selected",
  updateChats: "Update Chats",
  updating: "Updating...",
  chatsUpdatedSuccessfully: "Chats updated successfully",
  failedToUpdateChats: "Failed to update chats",
  noChatsAssigned: "No chats assigned",
  atLeastOneChatRequired: "At least one chat must be selected.",
  selectAtLeastOneChat: "Please select at least one chat before continuing.",
  manageChats: "Manage Chats",

  // Folders
  newFolder: "New Folder",
  folderName: "Folder name",
  createFolder: "Create Folder",
  rename: "Rename",
  moveTo: "Move to...",
  moveToFolder: "Move to folder",
  moveHere: "Move Here",
  moveItemDescription: "Select a destination for \"{name}\"",
  movedSuccessfully: "Moved successfully",
  searchFolders: "Search folders...",
  searchFiles: "Search files",
  collectionRoot: "Collection Root",
  noFoldersFound: "No folders found",
  deleteFolder: "Delete Folder",
  deleteFolderConfirmation: "This will permanently delete \"{name}\" and all its contents ({folders} subfolder(s) and {files} file(s)). This action cannot be undone.",
  failedToCreateFolder: "Failed to create folder",
  failedToRenameFolder: "Failed to rename folder",
  failedToDeleteFolder: "Failed to delete folder",
  failedToMove: "Failed to move item",
  movedFileSuccessfully: "File moved successfully",
  movedFilesSuccessfully: "{count} files moved successfully",
  movedFilesPartial: "{moved} of {total} files moved ({failed} unsuccessful)",
  movedNoFiles: "No files were moved",
  movedFolderSuccessfully: "Folder moved successfully",
  movedFoldersSuccessfully: "{count} folders moved successfully",
  movedFoldersPartial: "{moved} of {total} folders moved ({failed} unsuccessful)",
  movedNoFolders: "No folders were moved",
  deletedFolderSuccessfully: "Folder deleted successfully",
  deletedFileSuccessfully: "File deleted successfully",
  deletedFilesSuccessfully: "All {count} files deleted successfully",
  deletedFilesPartial: "{successful} of {total} files deleted, {failed} failed",
  folderSingular: "folder",
  folderPlural: "folders",
  fileSingular: "file",
  filePlural: "files",
  rootFolder: "Root",
  move: "Move",
  dropHereToMove: "Drop here to move",
  fileAlreadyExistsInAnotherFolder: "A file with this name already exists in another folder. Please rename it or delete the existing one first.",

  // Folder upload
  uploadFolder: "Upload Folder",
  uploadFolderSummary: "{files} files ({pdf} PDF, {txt} TXT, {md} MD) in {folders} subfolder(s)",
  invalidFilesInFolder: "Some files in this folder are not supported:",
  emptyFolder: "The folder is empty or contains no supported files",
  mixedDropError: "Please drop either files or a single folder, not both",
  creatingFolders: "Creating folders...",

  // Widget settings page
  widget: "Widget",
  widgetSettings: "Widget Settings",
  widgetSettingsDescription: "Customise your chat widget's appearance and behaviour.",
  theme: "Theme",
  colors: "Colours",
  primaryColor: "Primary",
  secondaryColor: "Secondary",
  launcher: "Launcher",
  launcherBackground: "Launcher background",
  launcherText: "Launcher text",
  launcherIcon: "Icon URL",
  launcherShape: "Shape",
  launcherShapePill: "Pill",
  launcherShapeCircle: "Circle",
  launcherSize: "Size",
  launcherCorner: "Corner",
  launcherCornerBottomRight: "Bottom-right",
  launcherCornerBottomLeft: "Bottom-left",
  launcherOffsetX: "Offset X",
  launcherOffsetY: "Offset Y",
  launcherAnimation: "Animation",
  launcherAnimationNone: "None",
  launcherAnimationFloat: "Float",
  launcherAnimationPulse: "Pulse",
  launcherAnimationBounce: "Bounce",
  logo: "Logo",
  logoUrl: "Logo URL",
  customCSS: "Custom CSS",
  customCSSPanel: "Custom CSS — Panel",
  customCSSLauncher: "Custom CSS — Launcher",
  customCSSPanelPlaceholder: ".kodly-ai-agent-panel {\n  padding: 12px;\n}",
  customCSSLauncherPlaceholder: ".kodly-ai-agent-launcher:hover {\n  transform: scale(1.1);\n}",
  // Launcher / panel CSS info modal
  infoLauncherTitle: "Launcher customization",
  infoPanelTitle: "Panel customization",
  infoStableClasses: "Stable classes",
  infoDataAttributes: "Data attributes",
  infoCssVars: "CSS custom properties",
  infoKeyframes: "Animation keyframes (redefinable)",
  infoCssExamples: "Examples",
  infoCopy: "Copy",
  infoCopied: "Copied!",
  infoClose: "Close",
  infoHelpLabelLauncher: "Help: launcher customization",
  infoHelpLabelPanel: "Help: panel customization",
  infoLauncherScopingNote:
    "Top-level selectors must start with .kodly-ai-agent-launcher. Allowed at-rules: @keyframes, @media, @supports. Anything else is blocked.",
  infoPanelHintsNote:
    "These hooks are stable today but may change with widget updates. For long-term stability, prefer the structured theme fields above (primary/secondary colors, logo, copy).",
  infoExampleFloatGlow: "Float animation + glow",
  infoExampleGlass: "Glassmorphism",
  infoExampleHoverScale: "Hover scale + shadow lift",
  infoExampleHeaderGradient: "Custom header background",
  infoExampleBubbleShape: "Message bubble shape",
  infoExampleCustomFont: "Custom font for messages",
  copy: "Copy",
  headerTitle: "Header title",
  headerTitlePlaceholder: "e.g. Virtual assistant",
  launcherTitle: "Launcher title",
  launcherTitlePlaceholder: "e.g. Need help?",
  botName: "Bot name",
  botNamePlaceholder: "e.g. Kodly",
  botAvatarUrl: "Bot avatar URL",
  welcomeTitle: "Welcome title",
  welcomeDescription: "Welcome description",
  defaultQuickReplies: "Default quick replies",
  pageContexts: "Page-specific quick replies",
  pageContextsHint:
    "The first context in the list whose patterns match the page wins. On save and on " +
    "import, the platform sorts the list so that the narrower context sits above the " +
    "broader one, for example /produto/lego* above /produto/*. In any other case where " +
    "two contexts match the same page, such as /produto/*oled* and /produto/tv-*, the " +
    "one higher in the list wins. To swap two of these, export, change their order in " +
    "the file and import it again. Pages matching no context get the default replies " +
    "above, in the language the page asked for. A context's own replies are not " +
    "per-language: they go out exactly as written, to every market this widget " +
    "serves. The preview beside this form always " +
    "shows the default replies, because it has no host page; to see a context, open " +
    "the widget on the site.",
  pageContext: "Context",
  pageContextName: "Name",
  pageContextNamePlaceholder: "e.g. Product",
  pageContextPatterns: "URL patterns",
  pageContextPatternsPlaceholder: "/produto/**\n/en/product/**",
  pageContextPatternsHint:
    "One pattern per line, each starting with /. Pasting a full URL works too: " +
    "its domain and query string are ignored. * matches within a segment, ** crosses /. " +
    "Trailing slash ignored; matching is case-insensitive. Note that /produto/** " +
    "requires a segment after it, so it does not match /produto on its own.",
  pageContextReplies: "Quick replies",

  // Page-contexts import and export (RPAA-246). Indexes arrive 0-based from the
  // backend and are shown 1-based, naming the entry in the FILE: a refused
  // import writes nothing, so the list on screen is still the old one.
  pageContextsExport: "Export",
  pageContextsExportHint: "Exports what is saved, not unsaved edits.",
  pageContextsExportFailed: "Could not export the page contexts.",
  pageContextsImport: "Import",
  pageContextsImportHint: "Replaces every page context of this chat.",
  pageContextsImportBlocked:
    "You have unsaved changes. Save or discard them before importing.",
  pageContextsImportTitle: "Import page contexts",
  pageContextsDragDrop: "Drag a JSON file here, or click to choose one.",
  pageContextsCountPlain: "{count} page contexts",
  pageContextsCountAtCap: "{count} page contexts: the maximum this chat allows.",
  pageContextsImportConfirm:
    "This chat has {current} page contexts. The file brings {incoming}, and importing " +
    "replaces every one of them.",
  pageContextsImportConfirmWithReplies:
    "This chat has {current} page contexts. The file brings {incoming}, and importing " +
    "replaces every one of them. It also replaces the Default quick replies.",
  pageContextsImportReplace: "Replace",
  pageContextsImporting: "Importing...",
  pageContextsImported: "{count} page contexts imported.",
  pageContextsFileMustBeJson: "The file must be a .json file.",
  pageContextsFileTooLarge: "The file is too large.",
  pageContextsFileUnreadable: "This file is not a page-contexts export.",
  pageContextsImportRefused: "The file was refused",
  pageContextsFileEntry: "file entry {index}",
  pageContextsImportFailed: "Could not import the file.",
  pageContextsWarningsTitle: "Check these before you rely on it",
  pageContextsWarningInert:
    "These contexts never show up, because they have no URL patterns or no quick " +
    "replies: {contexts}.",
  pageContextsWarningInertOne:
    "This context never shows up, because it has no URL patterns or no quick replies: " +
    "{contexts}.",
  pageContextsWarningCatchAll:
    "{shadowed} contexts never show up, because {context} catches every page and sits " +
    "above them.",
  pageContextsWarningCatchAllOne:
    "One context never shows up, because {context} catches every page and sits above it.",
  pageContextsWarningDuplicate:
    "Pattern {pattern} is in {contexts}. On those pages, only the one higher in the list " +
    "shows up.",
  pageContextsWarningChatMismatch:
    "This file was exported from another chat. The contexts were imported anyway.",
  pageContextsWarningReordered:
    "{count} contexts moved up the list, above another context that was taking pages " +
    "meant for them: {contexts}.",
  pageContextsWarningReorderedOne:
    "One context moved up the list, above another context that was taking pages meant " +
    "for it: {contexts}.",
  pageContextsNamed: "“{name}”",
  pageContextsNamedWithEntry: "“{name}” (entry {index})",
  pageContextsFileEntryLabel: "file entry {index}",
  pageContextsListAnd: " and ",
  pageContextsListAndMore: "{list} and {count} more",

  addPageContext: "Add context",
  previous: "Previous",
  next: "Next",
  pageContextsSearch: "Search by name or pattern",
  pageContextsCount: "contexts",
  pageContextsLoadError: "Could not load contexts.",
  pageContextsNoMatch: "No contexts match this search.",
  pageContextsSaveHint:
    "Each context saves on its own: the Save button below does not carry them.",
  savePageContext: "Save this context",
  pageContextAddFailed: "Could not add the context.",
  pageContextSaveFailed: "Could not save the context.",
  pageContextsOthersReordered:
    "Other contexts changed order so that none sits below one that covers it: {count}.",
  pageContextMovedUpOnSave:
    "The context moved up the list: the narrower context sits above the broader one.",
  pageContextMovedDownOnSave:
    "The context moved down the list: the narrower context sits above the broader one.",
  pageContextDeleteFailed: "Could not delete the context.",
  pageContextsUnsavedCount: "{count} context{plural} with unsaved edits.",
  pageContextsLeaveConfirm:
    "Some page contexts have unsaved edits. Switching chat discards them. Continue?",
  pageContextDeleteConfirm:
    'Are you sure you want to delete the context "{name}"? Its patterns and quick replies are deleted immediately. This cannot be undone.',
  removePageContext: "Remove context",
  patternSingular: "pattern",
  patternPlural: "patterns",
  replySingular: "reply",
  replyPlural: "replies",
  addQuickReply: "Add quick reply",
  moveUp: "Move up",
  moveDown: "Move down",
  tooManyQuickReplies: "Maximum 6 quick replies.",
  quickReplyPlaceholder: "Quick reply text",
  features: "Features",
  fileUpload: "File upload",
  fileUploadDescription: "Allow users to attach files in conversation with human agents.",
  audioMessages: "Audio messages",
  audioMessagesDescription: "Allow users to record and send voice notes.",
  productCardDelivery: "Delivery time on product cards",
  productCardDeliveryDescription:
    "Shows the delivery estimate on each product card, or the availability state when the product cannot ship yet.",
  productCardTagBadges: "Tag badge on product cards",
  productCardTagBadgesDescription:
    'Shows one campaign or product-state tag ("Pré-Venda", "Artigo de Exposição") on the card\'s top edge.',
  productCardDiscountBadge: "Discount badge on product cards",
  productCardDiscountBadgeDescription: "Shows the discount percentage over the product photo.",
  productCardHoverImage: "Second photo on hover",
  productCardHoverImageDescription:
    "Shows the product's second photo while the pointer rests on the card (desktop only).",
  productCardViewCta: '"View product" button on cards',
  productCardViewCtaDescription:
    "Adds a full-width button at the foot of the card, opening the product page. Hidden while the add-to-cart button is on.",
  cardLinksSameTab: "Open recommendations in the same page",
  cardLinksSameTabDescription:
    "Product and curated cards navigate the current page instead of opening a new tab, and the chat reopens over it. Only applies to links on your own site (on phones the chat reopens with one tap on the launcher).",
  previewPanel: "Panel",
  previewLauncher: "Launcher",
  previewWelcome: "Welcome",
  previewDesktop: "Desktop",
  previewMobile: "Mobile",
  previewInviteShow: "Show invite",
  previewInvitePlaceholder: "Invite message",
  previewInviteSample: "Need a hand choosing?",
  previewInviteHint: "Preview only. The invite text comes from your website, not from these settings.",
  reloadPreview: "Reload preview",
  previewUnavailable: "Preview unavailable",
  previewLanguage: "Preview in {language}.",
  previewLanguageOtherTab:
    "Preview in {language}. The {tab} tab has no preview of its own yet.",
  welcomeTitlePlaceholder: "e.g. Olá! 👋",
  welcomeDescriptionPlaceholder: "e.g. Em que podemos ajudar?",
  priceInfoNotice: "Legal price notice",
  priceInfoNoticePlaceholder: "e.g. Displayed prices are valid in the online store only...",
  priceInfoNoticeHint:
    "Shown in a popover behind the (i) icon next to prices on product cards. Leave empty to hide the icon.",
  resetToDefaults: "Reset to defaults",
  resetToDefaultsTooltip: "Restore all widget settings to defaults",
  resetField: "Reset to default",
  modifiedNotSaved: "Modified — not yet saved",
  confirmResetDefaults: "Reset all widget settings to defaults?",
  confirmResetDefaultsClearsTranslations:
    "The copy is cleared in every language, translations included.",
  confirmResetDefaultsKeepsPageContexts: "Page contexts stay as they are.",
  widgetSettingsSaved: "Widget settings saved",
  widgetSettingsSaveFailed: "Failed to save widget settings",
  widgetSettingsReloadFailed:
    "Imported, but the page could not reload. Refresh to see the new contexts.",
  widgetLanguage: "Widget interface language",
  widgetLanguageDescription:
    "Language of the widget's interface texts — not the language the assistant replies in.",
  widgetLocales: "Widget languages",
  widgetLocalesDescription:
    "The languages this widget serves. The embed script picks one with &language=. Each gets its own tab in Copy.",
  removeWidgetLocale: "Remove language",
  addWidgetLocale: "Add language",
  copyLanguageTab: "Language",
  manager: "Manager",
  assignRole: "Assign role",
  assignRoleDescription:
    "Only users with this Zitadel role (or administrators) can see and manage this folder and its contents. Leave empty to keep it open to everyone with Internal Procedures access.",
  requiredRolePlaceholder: "e.g. logistica",
  roleAssigned: "Folder role updated",
  failedToAssignRole: "Failed to update folder role",
  configurations: "Configurations",
  personalLandingDescription: "Quick access to the areas you manage",
  noAccessibleSections:
    "You don't have access to any management area yet. Contact your administrator.",

  // VTEX promotion sync (RPAA-229)
  vtexSyncTitle: "VTEX promotion sync",
  vtexSyncNow: "Sync now",
  vtexSyncStarted: "Sync started.",
  vtexSyncing: "Syncing...",
  vtexSyncFinished: "Sync finished. {count} to approve.",
  vtexSyncFinishedNothing: "Sync finished. Nothing to approve.",
  vtexSyncFinishedWithoutReport: "The sync ended with no result. Try again.",
  vtexSyncStillRunning: "The sync is still running. Come back in a moment.",
  vtexSyncFailed: "Could not start the sync",
  campaignRejectedForStore: "Campaign rejected for this store. The file stays because another store publishes it.",
  campaignsRejectedForStore: "{count} campaigns rejected for this store. The files stay because other stores publish them.",
  campaignRejectedAndRemoved: "Campaign rejected and file deleted. It will not come back on the next sync.",
  campaignsRejectedAndRemoved: "{count} campaigns rejected and files deleted. They will not come back on the next sync.",
  vtexSyncDisabled: "Automatic sync is turned off.",
  vtexViewLoadFailed: "Could not read the sync view. What is shown may be out of date.",
  vtexViewLoadRetry: "Try again",
  vtexChatIsNotAStore: "This chat is not a VTEX store. Pick another store above.",
  vtexChatIsNotAStoreAlone: "This chat is no longer a VTEX store.",
  vtexNeverSynced: "never synced",
  vtexSalesChannel: "Sales channel",
  vtexCondition_utm: "Needs a code",
  vtexCondition_audience: "One campaign only",
  vtexCondition_cluster: "One customer segment only",
  vtexCondition_partial_shipping: "May not cover all shipping",
  vtexSelectAll: "Select all",
  vtexSelectedCount: "{count} selected",
  vtexSelectedCountOne: "1 selected",
  vtexSelectUnconditional: "Select the {count} with no condition",
  vtexSelectUnconditionalOne: "Select the one with no condition",
  vtexConfirm: "Confirm",
  vtexConfirmApprove: "Approve {count}?",
  vtexConfirmApproveConditional: "Approve {count}, {conditional} of which carry a condition?",
  vtexConfirmReject: "Reject {count}?",
  vtexBulkApproved: "{count} campaigns approved.",
  vtexBulkRejected: "{count} campaigns rejected. You can undo the rejection under rejected.",
  vtexBulkRejectionsUndone: "{count} rejections undone. The promotions return to the approval queue on the next sync.",
  vtexBulkFailed: "{count} were not done. The list shows what is still undecided.",
  vtexQueuedBadge: "{count} awaiting approval",
  vtexQueuedTitle: "Awaiting approval",
  vtexQueuedEmpty: "Nothing awaiting approval.",
  vtexQueueUnknown: "No recent sync — the queue will be known after the next run.",
  vtexApprove: "Approve",
  vtexReject: "Reject",
  vtexStorePicker: "Store",
  vtexUndoRejection: "Undo rejection",
  vtexUndoAllRejections: "Undo the {count} rejections",
  vtexPromotionApproved: "Promotion approved. The agent reads it now.",
  vtexPromotionApproveFailed: "Could not approve the promotion",
  vtexPromotionRejected: "Promotion rejected. It will not come back on the next syncs.",
  vtexPromotionRejectFailed: "Could not reject the promotion",
  vtexRejectionUndone: "Rejection undone. The promotion returns to the approval queue on the next sync.",
  vtexPromotionNotRejected: "That promotion was not rejected",
  vtexUndoRejectionFailed: "Could not undo the rejection",
  vtexExcludedTitle: "Left out",
  vtexExcludedDescription: "Promotions VTEX had on this run that were left out. The reason is next to each one.",
  vtexRejectedTitle: "Rejected",
  vtexRejectedDescription: "Deleted by someone. They do not come back unless VTEX changes them.",
  vtexResurrectedTitle: "Back after rejection",
  vtexResurrectedDescription: "Came back on this run: VTEX changed them after they had been rejected.",
  vtexOverwrittenTitle: "Text replaced",
  vtexOverwrittenDescription: "On this run the sync rewrote text someone had edited by hand.",
  // The backend no longer sends this reason, but the Redis report has no expiry: one
  // written before this change still carries it. Without the label the row would show
  // the raw "wrong_channel" until the next sync.
  vtexReason_wrong_channel: "Another sales channel",
  vtexReason_no_effect: "Effect we cannot read",
  vtexReason_no_offer_text: "No description explaining the offer",
  vtexReason_detail_404: "VTEX no longer returns this promotion",
  vtexReason_malformed: "Malformed promotion in VTEX",
  vtexCurrentApproved: "approved",
  vtexCurrentQueued: "awaiting approval",
  vtexLastRun: "Last sync",

  // Analytics (RPAA-286)
  analyticsShowTable: "Show table",
  analyticsShowChart: "Show chart",
  analyticsDownloadTable: "Download this table",
  analyticsNoData: "No data in this period.",
  analytics: "Analytics",
  analyticsLoadFailed: "Could not load the analytics.",
  analyticsExport: "Export",
  "analyticsExportOption.conversations": "Conversations",
  "analyticsExportOption.daily": "Analytics data",
  "analyticsExportOption.firstQuestions": "Content and assistant",
  "analyticsExportFailed.conversations": "Could not export the conversations.",
  "analyticsExportFailed.daily": "Could not export the analytics data.",
  "analyticsExportFailed.firstQuestions": "Could not export the content and the assistant.",
  analyticsKodlyDirectOnly: "Analytics only covers chats served by Kodly directly. This chat is not served that way yet, so there is nothing to show.",
  analyticsNotAvailable: "n/a",
  analyticsCategory: "Category",
  analyticsGroup: "Group",
  analyticsCount: "Count",
  analyticsUnattributed: "No information",
  analyticsNoComparison: "no comparison",
  analyticsAbout: "About {title}",
  analyticsPeriod: "Period",
  analyticsLastDays: "Last {count} days",
  analyticsCustomPeriod: "Pick dates",
  analyticsPeriodRange: "{from} to {to}",
  analyticsChooseDay: "Choose",
  analyticsDayUnavailable: "Not available",
  analyticsPreviousMonth: "Previous month",
  analyticsNextMonth: "Next month",
  analyticsTimeZoneNote: "Days and hours in {tz}.",
  analyticsPresetEndsYesterday: "The 7, 30 and 90 day periods end yesterday, the last full day.",
  analyticsHeadline: "Main numbers",
  analyticsNeedsAttention: "Needs attention",
  analyticsPageViews: "Page views",
  analyticsConversations: "Conversations",
  analyticsCustomerMessages: "Customer messages",
  analyticsStartedWithSuggestion: "Conversations started from a suggestion",
  analyticsWithoutOperator: "Conversations without an operator",
  analyticsFunnel: "Funnel",
  "analyticsFunnel.page_views": "Page views",
  "analyticsFunnel.opens": "Opens",
  "analyticsFunnel.conversations": "Conversations",
  "analyticsFunnel.two_or_more": "With 2 or more messages",
  analyticsFunnelDockedNote: "In a chat embedded in the page the assistant is always open, so the funnel starts at the conversations.",
  analyticsOpensBySource: "Opens by origin",
  analyticsMessagesBySource: "Customer messages by origin",
  analyticsInvites: "Invitations",
  analyticsInvitesShown: "Invitations shown",
  analyticsInvitesOpens: "Opens from an invitation",
  analyticsInvitesRate: "Invitation open rate",
  "analytics.source.typed": "Typed",
  "analytics.source.quick_reply": "Suggestion",
  "analytics.source.choice": "Assistant option",
  "analytics.source.invite": "Invitation",
  "analytics.source.invite_auto": "Invitation, sent on click",
  "analytics.source.host": "By the page",
  "analytics.source.launcher": "Launcher button",
  "analytics.source.restore": "Reopened on the next page",
  "analytics.source.docked": "Embedded in the page",
  "analytics.source.voice": "Voice message",
  analyticsCompositionNote: "Suggestions, assistant options, invitations and messages sent by the page are text the customer did not type. A voice message counts as typed: they are the customer's own words.",
  "analytics.composition.only_typed": "Only typed or spoken",
  "analytics.composition.mixed": "Mixed",
  "analytics.composition.only_suggestions": "Only suggestions",
  analyticsWithoutOperatorNote: "Without an operator is counted over conversations, not requests: one conversation can ask for an operator twice.",
  "analytics.via.inactivity": "Closed on their own, after inactivity",
  "analytics.via.customer_header": "The customer closed it from the header",
  "analytics.via.customer_agent_button": "The customer ended it with the button",
  "analytics.via.operator": "Closed by an operator",
  "analytics.via.contact_merged": "Contact merged into another",
  "analytics.via.open": "Still open",
  "analytics.via.unknown": "Unknown",
  "analytics.profile.known": "Recognised customer",
  "analytics.profile.empty": "No saved profile",
  "analytics.profile.no_cookie": "No cookie",
  "analytics.profile.failed": "Profile lookup failed",
  "analytics.profile.unreadable": "Unreadable profile cookie",
  "analytics.size_search.used": "With the saved profile",
  "analytics.size_search.stated": "With what the customer said",
  "analytics.size_search.missing": "No profile, asked for sizes",
  "analytics.size_search.no_gender": "Profile without a single gender, asked who it is for",
  "analytics.size_search.section": "No size for the page's section, asked for it",
  "analytics.outcome.success": "Answered by an operator",
  "analytics.outcome.no_agent_available": "No operator available",
  "analytics.outcome.agents_busy": "Operators busy",
  "analytics.outcome.no_handoff_target": "No operator inbox set up",
  "analytics.outcome.error": "Error during the transfer",
  "analytics.hours.in": "Within hours",
  "analytics.hours.out": "Outside hours",
  analyticsOperatorReply: "Time until the operator replied",
  analyticsOperatorReplyNote: "Measured when the customer received the reply, not when the operator wrote it. Conversations still waiting: {waiting}.",
  "analytics.bucket.0-1m": "Under 1 min",
  "analytics.bucket.1-5m": "1 to 5 min",
  "analytics.bucket.5-15m": "5 to 15 min",
  "analytics.bucket.15-60m": "15 to 60 min",
  "analytics.bucket.60m+": "Over 1 hour",
  "analytics.bucket.none": "No reply",
  analyticsSuggestions: "Suggestions by page context",
  analyticsSuggestionsNote: "Includes the suggestions nobody used in this period.",
  analyticsWelcomeContext: "Welcome screen",
  analyticsUnnamedContext: "Page context without a name",
  analyticsDeletedContext: "Deleted context",
  analyticsSuggestion: "Suggestion",
  analyticsUses: "Uses",
  analyticsNotMeasured: "Not measured",
  analyticsNotMeasuredNote: "Uses not measured, from suggestions too long to count one by one (over 1024 bytes): {count}.",
  analyticsNoLongerConfigured: "no longer configured",
  analyticsMoreContexts: "Show more contexts ({shown} of {total})",
  analyticsNeverUsed: "Never used",
  analyticsShowAllQuestions: "Show all {count}",
  analyticsShowFewerQuestions: "Show fewer",
  analyticsRetry: "Try again",
  analyticsTopProducts: "Most shown products",
  analyticsTopCards: "Most shown cards",
  analyticsTopProductsLimited: "These are the {count} most shown; the Export (Content and assistant) has them all.",
  analyticsTopProductsLead: "{products} different products shown in cards.",
  analyticsTopProductsLeadOne: "1 product shown in cards.",
  analyticsTopCurated: "Most shown curated cards",
  analyticsTopCuratedLead: "The most shown was {title}, {count} times.",
  analyticsTopCuratedLeadOne: "The most shown was {title}, once.",
  analyticsCardsShownNote: "Customer conversations only, without bots. A card counts in the hour it was shown, and a click or an add to cart request in the hour it happened. The funnel above counts cards when the assistant sends them, so the totals can differ slightly.",
  analyticsCardsShownNoteNoCart: "Customer conversations only, without bots. A card counts in the hour it was shown, and a click in the hour it happened. The funnel above counts cards when the assistant sends them, so the totals can differ slightly.",
  analyticsClicksSinceNoCart: "Clicks are counted since {date}.",
  analyticsClicksSince: "Clicks and add to cart requests are counted since {date}.",
  analyticsClicksNotRecorded: "This chat has not recorded any clicks yet.",
  analyticsNoCardsShown: "No cards shown in this period.",
  analyticsProduct: "Product",
  analyticsCuratedCard: "Card",
  analyticsScope: "Scope",
  analyticsShownColumn: "Shown",
  analyticsClicksColumn: "Clicks",
  analyticsCartColumn: "Add to cart",
  analyticsNeverShown: "Never shown",
  analyticsCardNoLongerConfigured: "no longer configured",
  analyticsShowAllCards: "Show all {count}",
  analyticsShowFewerCards: "Show fewer",
  analyticsChoices: "Guided choices",
  analyticsChoicesNote: "Only the questions asked with options to tap count. The option tapped is exact. A reply that was typed is not read, so it says nothing about which option it meant.",
  analyticsChoicesFlow: "Conversations that reached each question: {scope}",
  analyticsChoicesFlowNoScope: "Conversations that reached each question: without a scope",
  analyticsChoicesAnsweredLabel: "answered {name}",
  analyticsChoicesAnsweredColumn: "Answered the question before",
  analyticsChoicesAsked: "Asked {count} times",
  analyticsChoicesAskedOne: "Asked once",
  analyticsChoicesNotAsked: "Not asked in this period.",
  analyticsChoicesOptions: "Options tapped in {name}",
  analyticsChoicesNoQuestions: "No question asked with options to tap in this period.",
  analyticsChoicesReached: "Reached {count} conversations",
  analyticsChoicesReachedOne: "Reached 1 conversation",
  analyticsChoicesAnswered: "{count} answered",
  analyticsChoicesAnsweredOne: "1 answered",
  analyticsChoicesUnknownOption: "Option not identified",
  "analyticsChoicesAnswer.tapped": "Tapped an option",
  "analyticsChoicesAnswer.typed_reply": "Replied without tapping",
  "analyticsChoicesAnswer.no_reply": "Did not reply",
  "analyticsChoicesAnswer.open": "Conversation still open",
  analyticsToolsUsed: "Tools used",
  analyticsToolsLead: "{calls} tool calls, {share} of them failed.",
  analyticsToolsLeadOne: "1 tool call.",
  analyticsToolsLeadOneFailed: "1 tool call, which failed.",
  analyticsToolsLeadNoFailures: "{calls} tool calls, none of them failed.",
  analyticsToolsNote: "A call failed when the tool raised an error or did not finish; a call whose tool replies that it found nothing did not fail. The calls of a reply that failed before its first message are not counted. The typical time is the median.",
  analyticsNoToolCalls: "The assistant used no tools in this period.",
  analyticsTool: "Tool",
  analyticsCallsColumn: "Calls",
  analyticsFailedColumn: "Failed",
  analyticsTypicalTime: "Typical time",
  "analyticsTool.general_information_tool": "Search general information",
  "analyticsTool.campaigns_search_tool": "Search campaigns",
  "analyticsTool.product_recommendation_tool": "Search products",
  "analyticsTool.product_details_tool": "Product details",
  "analyticsTool.send_product_cards_tool": "Show product cards",
  "analyticsTool.send_curated_cards_tool": "Show curated cards",
  "analyticsTool.send_choice_options_tool": "Show choice options",
  "analyticsTool.save_customer_preference_tool": "Save customer preferences",
  "analyticsTool.human_handoff_tool": "Hand off to an operator",
  "analyticsTool.order_inquiry_tool.lookup": "Look up orders",
  "analyticsTool.order_inquiry_tool.faq": "Answer about orders",
  "analyticsTool.return_inquiry_tool": "Look up returns",
  "analyticsTool.returnable_items_tool": "Returnable items",
  "analyticsTool.store_stock_tool": "Store stock",
  "analyticsTool.warehouse_stock_tool": "Warehouse stock",
  "analyticsTool.send_end_conversation_button_tool": "Offer to end the conversation",
  "analyticsTool.internal_procedures_tool": "Search internal procedures",
  analyticsTypedQuestions: "Most frequent typed questions",
  analyticsTypedQuestionsNote: "The first message of each conversation, shown only when {count} or more different people asked it. It follows the conversation retention.",
  analyticsLimitedByRetention: "Conversations are kept for {days} days, so this list only covers that part of the period.",
  analyticsLimitedByRetentionOneDay: "Conversations are kept for 1 day, so this list only covers that part of the period.",
  analyticsNoTypedQuestions: "No question reached {count} different people in this period.",
  analyticsQuestion: "Question",
  analyticsLinks: "Links",
  analyticsConversationCount: "Conversations",
  analyticsContactCount: "People",
  analyticsOpenConversations: "View conversations ({count})",
  analyticsViewConversations: "View conversations",
  analyticsCsatViewOperatorConversations: "View conversations of {name}",
  analyticsRecentConversations: "The {count} most recent conversations",
  analyticsRecentConversation: "The most recent conversation",
  analyticsCardsShown: "Products shown in cards",
  analyticsCardClicks: "Card clicks",
  analyticsAddToCart: "Add to cart requests",
  analyticsAddToCartNote: "Add to cart requests are what the assistant sent to the page; the page does not tell us whether the product was added.",
  "analyticsOrderInquiries.lookup": "Order lookups by result",
  "analyticsOrderInquiries.faq": "Order questions by result",
  "analytics.order_outcome.found": "Found",
  "analytics.order_outcome.found_delivered": "Found, delivered",
  "analytics.order_outcome.found_in_transit": "Found, in transit",
  "analytics.order_outcome.found_cancelled": "Found, cancelled",
  "analytics.order_outcome.found_other": "Found, other status",
  "analytics.order_outcome.not_found": "Not found",
  "analytics.order_outcome.no_orders": "No orders",
  "analytics.order_outcome.not_authenticated": "Customer not signed in",
  "analytics.order_outcome.missing_order_id": "No order number",
  "analytics.order_outcome.ownership_mismatch": "Order of another customer",
  "analytics.order_outcome.asked_for_clarification": "Asked for details",
  "analytics.order_outcome.info_returned": "Information returned",
  "analytics.order_outcome.fallback": "Default message, no information found",
  "analytics.order_outcome.no_strategy": "Order lookup not set up",
  "analytics.order_outcome.error": "Error",
  analyticsCampaignSearches: "Campaign searches",
  analyticsCampaignSearchesNote: "Without result means no campaign was close enough to what was asked, not that the campaign does not exist.",
  "analytics.matched.true": "With result",
  "analytics.matched.false": "Without result",
  analyticsWhen: "When",
  analyticsDevicesNote: "An iPad in Safari counts as a computer in page views and as a tablet in conversations.",
  analyticsConversationsLanguage: "Conversations by page language",
  "analytics.device.mobile": "Phone",
  "analytics.device.desktop": "Computer",
  "analytics.device.tablet": "Tablet",
  "analytics.device.other": "Other",
  "analytics.os.other": "Other",
  "analytics.browser.other": "Other",
  "analytics.language.pt": "Portuguese",
  "analytics.language.en": "English",
  "analytics.language.es": "Spanish",
  "analytics.language.fr": "French",
  "analytics.language.de": "German",
  "analytics.language.it": "Italian",
  analyticsTechnical: "Technical layer (Kodly)",
  analyticsTechnicalNotes: "What each number counts: a page view is a full load of the page, so in a single page app it counts fewer than the pages people see. Opens are only recorded since the version that measures them, and before it the opens of signed-in visitors were not measured at all.",
  analyticsJobsOnTime: "Counters and conversations are up to date.",
  analyticsCountersLate: "The counters have not been written for more than 5 minutes.",
  analyticsConversationsLate: "The conversation numbers have not been recomputed for more than 35 minutes.",
  analyticsAutoCloseSettings: "Automatic close in the bot phase: {bot}. In the human phase: {human}. Hours settle after {frozen} hours.",
  analyticsAutoCloseOn: "on, after {hours} h",
  analyticsAutoCloseOff: "off",
  "analytics.warning.bot_auto_close_off": "Automatic close is off in the bot phase: conversations stay open and the page counts fewer than there were.",
  "analytics.warning.bot_window_above_limit": "The bot phase window is longer than the hours the numbers take to settle: conversations freeze as still open.",
  "analytics.warning.human_auto_close_off": "Automatic close is off in the human phase, and this chat had conversations with an operator.",
  "analytics.warning.human_window_above_limit": "The human phase window is longer than the hours the numbers take to settle.",
  analyticsBots: "Page views from bots",
  analyticsBotOpens: "Opens from bots",
  analyticsBotConversations: "Conversations from bots",
  analyticsRestoreOpens: "Assistant that reopened on the next page",
  analyticsDockedOpens: "Opens of an embedded chat",
  analyticsSearchResults: "Products found by search",
  analyticsFallbackReplies: "Failed turns (error message sent)",
  analyticsFallbackRepliesNote: "Turns where the assistant could not answer and the configured error message was sent instead, for example when the audio was unavailable or a setting was missing. It is not the assistant saying it does not know.",
  analyticsPageViewsBrowser: "Page views by browser",
  analyticsPageViewsOrigin: "Page views by origin",
  analyticsOriginNote: "Opens have no origin: dividing the opens by the page views of one origin puts the test environments in the numerator.",
  analyticsOpensBrowser: "Opens by browser",
  analyticsConversationsBrowser: "Conversations by browser and version",
  analyticsConversationsOsVersion: "Conversations by system and version",
  analyticsConversationsOrigin: "Conversations by store origin",
  analyticsReplyLatency: "Time to the first reply",
  analyticsReplyLatencyDistribution: "Time to the first reply, by interval",
  analyticsLatencyMedian: "Time to the first reply (median)",
  analyticsLatencyP90: "Time to the first reply (p90)",
  analyticsLatencyReplies: "{count} replies measured",
  analyticsLatencyRepliesOne: "1 reply measured",
  analyticsLatencyP90Context: "90% of replies arrived sooner",
  analyticsLatencyEvolution: "Time to the first reply, day by day",
  analyticsLatencyEvolutionByHour: "Time to the first reply, hour by hour",
  analyticsMedian: "Median",
  analyticsP90: "p90",
  analyticsLatencyUnder: "Under {to} s",
  analyticsLatencyRange: "{from} to {to} s",
  analyticsLatencyOver: "Over {from} s",
  analyticsSecondsOrMore: "{value} s or more",
  analyticsVoiceTitle: "Voice messages",
  analyticsVoiceFigures: "Voice message figures",
  analyticsVoiceConversations: "Conversations with a voice message",
  analyticsVoiceConversationsShare: "{pct}% of conversations",
  analyticsVoiceMedian: "Median length",
  analyticsVoiceMeasured: "{count} measured",
  analyticsVoiceMeasuredOne: "1 measured",
  analyticsVoiceDistribution: "Length of voice messages",
  analyticsVoiceInfo: "Length and the voice messages that did not reach the assistant only exist since the version that measures them.",
  analyticsVoiceFailures: "Voice messages that did not reach the assistant",
  analyticsVoiceFailuresDetail: "{pct}% of those sent to the assistant",
  analyticsVoiceFailureReasons: "Why they did not get there",
  analyticsVoiceFailuresNote: "They also count as failed turns when the chat has the audio unavailable message.",
  "analytics.voice_outcome.failed_fetch": "The audio could not be read",
  "analytics.voice_outcome.rejected_format": "Format not accepted",
  "analytics.voice_outcome.failed_normalize": "The audio could not be converted",
  analyticsCountersUpdated: "Counters updated on {date}.",
  analyticsCountersNotUpdated: "Counters not updated yet.",
  analyticsConversationsUpdated: "Conversation numbers updated on {date}.",
  analyticsConversationsNotUpdated: "Conversation numbers not updated yet.",
  analyticsDataSince: "Data since {date}.",
  analyticsPrecision: "Counts can fall slightly short if the cache loses counters under memory pressure.",
  "analyticsOpenRate": "Open rate",
  "analyticsKpiPreviousAndPerDay": "{previous} in the previous period · {perDay} a day",
  "analyticsKpiPrevious": "{previous} in the previous period",
  "analyticsKpiOpenCount": "{count} opens",
  "analyticsKpiOpenCountOne": "1 open",
  "analyticsKpiPageViewCount": "{count} page views",
  "analyticsKpiPageViewCountOne": "1 page view",
  "analyticsLessThan": "less than {value}",
  "analyticsMissedInHoursNoteOne": "{share} of the only request made within business hours",
  "analyticsHighlight.noOperatorInHoursOne": "operator request within business hours found nobody available.",
  "analyticsKpiOpenRateContext": "{opens} in {views}",
  "analyticsKpiWithOperatorContext": "{count} conversations reached an operator",
  "analyticsKpiWithOperatorContextOne": "1 conversation reached an operator",
  "analyticsKpiConversationCount": "{count} conversations",
  "analyticsKpiConversationCountOne": "1 conversation",
  "analyticsMostTypedQuestion": "Most frequent typed question",
  "analyticsMostUsedSuggestion": "Most used suggestion",
  "analyticsNeverUsedSuggestions": "Suggestions never used",
  "analyticsKpiUses": "{count} uses",
  "analyticsKpiUsesOne": "1 use",
  "analyticsHighlights": "Highlights",
  "analyticsHighlight.noOperatorInHours": "operator requests within business hours found nobody available.",
  "analyticsHighlight.handoffPeakOutsideHours": "is the hour with the most operator requests ({count}), outside business hours.",
  "analyticsHighlight.campaignsWithoutResult": "of campaign searches found no campaign ({count} of {total}).",
  "analyticsHighlight.unusedSuggestion": "uses of the suggestion “{label}” in this period.",
  "analyticsHighlight.unusedSuggestions": "configured suggestions had no use in this period.",
  "analyticsHighlight.conversationsChange": "conversations compared with the previous period.",
  "analyticsHighlight.peakDay": "conversations on {day}, the busiest day of the period.",
  "analyticsGoTo.overview": "See the evolution",
  "analyticsGoTo.conversations": "Go to Conversations",
  "analyticsGoTo.assistant": "Go to Assistant",
  "analyticsGoTo.content": "Go to Content",
  "analyticsFunnelRate.opens": "open the assistant",
  "analyticsFunnelRate.conversations": "start a conversation",
  "analyticsFunnelRate.two_or_more": "carry on",
  "analyticsFunnelRateColumn": "Share of the step before",
  "analyticsEvolution": "Evolution",
  "analyticsMetric": "Metric",
  "analyticsOpens": "Opens",
  "analyticsHandoffRequests": "Operator requests",
  "analyticsDay": "Day",
  "analyticsHour": "Hour",
  "analyticsCurrentPeriod": "This period",
  "analyticsPreviousPeriodShort": "Previous period",
  "analyticsYesterday": "Yesterday",
  "analyticsToday": "Today",
  "analyticsPreviousDay": "Previous day",
  "analyticsUntilTime": "{label} until {time}",
  "analyticsPreviousPeriodDay": "Previous period day",
  "analyticsPreviousPeriodHour": "Previous period hour",
  "analyticsPeak": "Peak: {day}, {value}",
  "analyticsTodayPartial": "The last day is still going.",
  "analyticsHourPartial": "The fainter bar is the hour still going.",
  "analyticsHourStillGoing": "still going",
  "analyticsStarts": "How they start",
  "analyticsStartsLead": "{typed}% start with typed text and {suggestion}% with a suggestion.",
  "analyticsStartsLeadWithVoice": "{typed}% start with typed text, {suggestion}% with a suggestion and {voice}% with a voice message.",
  "analyticsCompositionTitle": "How they are written",
  "analyticsEndingsTitle": "How they end",
  "analyticsCustomerProfile": "Customer profile",
  "analyticsCustomerProfileNote": "The profile comes from the site's identity cookie. A conversation counts as recognised when the assistant had the customer's profile in at least one message. Older conversations did not record it.",
  "analyticsConversationsByProfile": "Conversations by profile",
  "analyticsProfileLead": "{share}% of conversations had the customer's profile.",
  "analyticsSizeSearches": "Searches by size",
  "analyticsSizeSearchesLead": "In {share}% of searches by size the assistant asked the customer for their sizes.",
  "analyticsSizeSearchesNote": "Counts every search the assistant ran, including those in an attempt it repeated after an error.",
  "analyticsDayByDay": "Day by day",
  "analyticsHourByHour": "Hour by hour",
  "analyticsHowTheyClosed": "How they were closed",
  "analyticsEndingsLeadNoOperator": "{inactivity}% closed on their own after inactivity and {customer}% were closed by the customer.",
  "analyticsEndingsLead": "{inactivity}% closed on their own after inactivity, {customer}% were closed by the customer and {operator}% by an operator.",
  "analyticsOperators": "Operators",
  "analyticsConversationsStarted": "Conversations started",
  "analyticsHowTheyStartAndWrite": "How they start and how they are written",
  "analyticsOperatorsEvolution": "Requests and requests served",
  "analyticsOperatorServed": "Served by an operator",
  "analyticsShareOfRequests": "{share} of operator requests",
  "analyticsMissedInHours": "Within business hours, nobody available",
  "analyticsMissedInHoursNote": "{share} of the {count} requests made within business hours",
  "analyticsHandoffOutcomeTitle": "What happened to the requests",
  "analyticsHoursIn": "Within business hours: {count}",
  "analyticsHoursOut": "Outside business hours: {count}",
  "analyticsHoursUnattributed": "No information: {count}",
  "analyticsReplyTimeLead": "{share} of conversations with an operator got a reply in under 5 minutes.",
  "analyticsWhenLead": "The busiest day for {noun} is {day} ({dayCount}), and the busiest hour is {hour}h ({hourCount}).",
  "analyticsWhenLeadOutside": "The busiest day for {noun} is {day} ({dayCount}), and the busiest hour is {hour}h ({hourCount}), already outside business hours.",
  "analyticsWhenLeadHour": "The busiest hour for {noun} is {hour}h ({hourCount}).",
  "analyticsWhenLeadHourOutside": "The busiest hour for {noun} is {hour}h ({hourCount}), already outside business hours.",
  "analyticsWhenNoun.conversations": "conversations",
  "analyticsWhenNoun.handoff_requests": "operator requests",
  "analyticsWhenShow": "Show",
  "analyticsByWeekday": "By day of the week",
  "analyticsByHour": "By hour of the day",
  "analyticsServiceHours": "Business hours: {days}, {start}h to {end}h",
  "analyticsDaysRange": "{from} to {to}",
  "analyticsDeviceTitle": "Device",
  "analyticsSystemTitle": "System",
  "analyticsAudience.page_views": "Page views",
  "analyticsAudience.conversations": "Conversations",
  "analyticsCardsTitle": "Product cards",
  "analyticsPer100CardsColumn": "Per 100 cards",
  "analyticsTab.overview": "Overview",
  "analyticsTab.adoption": "Adoption",
  "analyticsTab.conversations": "Conversations",
  "analyticsTab.content": "Content",
  "analyticsTab.assistant": "Assistant",
  "analyticsTab.technical": "Technical",
  "analyticsSections": "Analytics sections",
  "analyticsComparedWithShort": "Compared with {from} to {to}",
  "analyticsComparedWithYesterday": "Compared with yesterday",
  "analyticsComparedWithYesterdayUntil": "Compared with yesterday until {time}",
  "analyticsComparedWithUntil": "Compared with {from} to {to} until {time}",
  "analyticsComparedWithDay": "Compared with {day}",
  "analyticsCutNote": "Conversation, open and click numbers are compared up to the last recalculation, at {time}.",
  "analyticsCutNoteOtherDay": "Conversation, open and click numbers are compared up to the last recalculation, on {day} at {time}.",
  "analyticsNoComparisonBefore": "No comparison: data starts on {date}",
  "analyticsAboutPeriod": "About the period",
  "analyticsNoDataYet": "There is no data for this chat yet.",
  "analyticsNoDataBefore": "There is no data in this period yet. Data starts on {date}.",
  "analyticsShowSince": "Show from {date}",
  "analyticsKpiInvitesShown": "{shown} invitation{plural} shown",
  "analyticsWhereTheyOpen": "Where they open",
  "analyticsOpensLead": "{share}% of opens come from the launcher button.",
  "analyticsWhoTheyAre": "Who they are",
  "analyticsClicksPer100": "Clicks per 100 cards",
  "analyticsCartPer100": "Add to cart per 100 cards",
  "analyticsCampaignsWithoutResult": "Campaign searches without result",
  "analyticsOrderInquiriesCount.lookup": "Order lookups",
  "analyticsOrderInquiriesCount.faq": "Order questions",
  "analyticsKpiClicks": "{clicks} click{plural}",
  "analyticsKpiCartRequests": "{requests} add to cart request{plural}",
  "analyticsKpiCampaignSearches": "{without} of {total} searches",
  "analyticsKpiCampaignSearchesOne": "{without} of {total} search",
  "analyticsPer100CardsLabel": "per 100 cards",
  "analyticsCardsToCart": "From cards to cart",
  "analyticsCardsToCartLead": "{clicks} clicks and {carts} add to cart requests per 100 cards.",
  "analyticsCardsAndClicks": "Cards and clicks",
  "analyticsCardsLead": "{clicks} clicks per 100 cards.",
  "analyticsOrders": "Orders",
  "analyticsCampaigns": "Campaigns",
  "analyticsCampaignsLead": "{share} of campaign searches found no campaign.",
  "analyticsOrdersAndCampaigns": "Orders and campaigns",
  "analyticsOrdersAndCampaignsLead.lookup": "{orders} order lookups; {share} of campaign searches found no campaign.",
  "analyticsOrdersAndCampaignsLead.faq": "{orders} order questions; {share} of campaign searches found no campaign.",
  "analyticsOrdersAndCampaignsLeadOne.lookup": "1 order lookup; {share} of campaign searches found no campaign.",
  "analyticsOrdersAndCampaignsLeadOne.faq": "1 order question; {share} of campaign searches found no campaign.",
  "analyticsOrdersLead.lookup": "{orders} order lookups.",
  "analyticsOrdersLead.faq": "{orders} order questions.",
  "analyticsOrdersLeadOne.lookup": "1 order lookup.",
  "analyticsOrdersLeadOne.faq": "1 order question.",
  "analyticsCsatTitle": "Customer rating",
  "analyticsCsatNumbers": "Customer rating in numbers",
  "analyticsCsatNote": "Ratings of the conversations closed in the period. Each one counts on the day the conversation closed, even when it comes later, so the most recent days can still receive ratings.",
  "analyticsCsatNoRequests": "No rating requests in this period.",
  "analyticsCsatTurnedOff": "The rating request was turned off in this chat. These numbers come from the requests made before.",
  "analyticsCsatResponseRate": "Response rate",
  "analyticsCsatResponseRateContext": "{ratings} out of {requested}",
  "analyticsCsatRatingCount": "{count} ratings",
  "analyticsCsatRatingCountOne": "1 rating",
  "analyticsCsatRequestCount": "{count} requests",
  "analyticsCsatRequestCountOne": "1 request",
  "analyticsCsatSatisfaction": "Satisfaction",
  "analyticsCsatSatisfactionContext": "4 and 5 star ratings",
  "analyticsCsatAverage": "Average",
  "analyticsCsatAverageValue": "{value} out of 5",
  "analyticsCsatAverageContext": "over {ratings}",
  "analyticsCsatWithComment": "With a comment",
  "analyticsCsatWithCommentContext": "{share} of the ratings",
  "analyticsCsatWithCommentNote": "The comment is in the conversation, for those who can open it.",
  "analyticsCsatDistribution": "Ratings by stars",
  "analyticsCsatSeeByStars": "See the conversations with",
  "analytics.rating.1": "1 star",
  "analytics.rating.2": "2 stars",
  "analytics.rating.3": "3 stars",
  "analytics.rating.4": "4 stars",
  "analytics.rating.5": "5 stars",
  "analyticsCsatRatingsFrom": "Ratings from",
  "analyticsCsatGroup.all": "All",
  "analyticsCsatGroup.with_operator": "With an operator",
  "analyticsCsatGroup.assistant_only": "Assistant only",
  "analyticsCsatGroups": "With an operator and assistant only",
  "analyticsCsatRequests": "Requests",
  "analyticsCsatRatings": "Ratings",
  "analyticsCsatByOperator": "By operator",
  "analyticsCsatOperator": "Operator",
  "analyticsCsatByOperatorNote": "Sorted by the number of ratings: a small sample is not a ranking of the operator.",
  "analyticsCsatByOperatorEmpty": "No conversation with an operator was rated in this period.",
  "analyticsCsatAssistantOnlyNote": "With the assistant, the request only goes out when customers end the conversation themselves: these ratings come from those who ended it on purpose.",
  "analyticsSignedIn": "Signed in",
  "analyticsSessionsLead": "{share}% of conversations were with signed-in customers.",
  "analyticsSignedInConversations": "Conversations with a signed-in customer",
  "analyticsSignedInCustomers": "Signed-in customers",
  "analyticsSignedInCustomersNote": "Each customer counts once in the period, however many conversations they had.",
  "analyticsSignedInCustomersRetention": "Only the stored conversations: retention deletes those older than {days} days.",
  "analyticsSignedInCustomersRetentionOneDay": "Only the stored conversations: retention deletes those older than 1 day.",
  "analyticsSignedInCustomersNoPrevious": "The previous period is no longer stored: retention deletes conversations older than {days} days.",
  "analyticsSignedInCustomersNoPreviousOneDay": "The previous period is no longer stored: retention deletes conversations older than 1 day.",
  "analyticsSignedInNumbers": "Signed in, in numbers",
  "analyticsSignInTitle": "When they signed in",
  "analyticsSignInNote": "During the conversation means the customer signed in while it was open. A conversation that closed anonymous stays anonymous, even if the customer signs in later.",
  "analyticsSignInBefore": "{count} conversations in this period are from before sign-ins were recorded, which started on {date}, and are not included here: for those we only know whether the customer is signed in today.",
  "analyticsSignInBeforeOne": "1 conversation in this period is from before sign-ins were recorded, which started on {date}, and is not included here: for it we only know whether the customer is signed in today.",
  "analyticsSignInBeforeAll": "The conversations in this period are from before sign-ins were recorded: for those we only know whether the customer is signed in today.",
  "analyticsSignedInEvolution": "Signed-in conversations over time",
  "analyticsSignedInShare": "Signed in",
  "analytics.sign_in.at_start": "From the start",
  "analytics.sign_in.during": "During the conversation",
  "analytics.sign_in.anonymous": "Anonymous",
  "analyticsPagesTitle": "Pages with the most conversations",
  "analyticsPagesLead": "{share}% of conversations started on the {count} pages with the most conversations.",
  "analyticsPagesLeadOne": "{share}% of conversations started on the page with the most conversations.",
  "analyticsPagesStart": "Where they started",
  "analyticsPagesThrough": "Where they wrote from",
  "analyticsPagesOthers": "Other pages",
  "analyticsPagesWithout": "No page",
  "analyticsPagesNote": "The page path, without parameters. Long numbers and codes show as :id, and emails as :email. No page means the customer only sent voice or images, or wrote from outside the site.",
  "analyticsPagesThroughNote": "A conversation counts on every page the customer wrote from, so the percentages do not add up to 100%.",
  "analyticsPagesSince": "Page data starts on {date}.",
  "analyticsPagesShareColumn": "Percentage of conversations",
};
